// ============================================
// File: src/components/teams/TeamDetails/index.jsx
// Orchestrator component — composes all sections, keeps live hooks tidy
// ============================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { serverTimestamp, doc, getDoc, updateDoc, collection, query, where } from 'firebase/firestore';
import { db, functions } from '../../../firebase-client';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import { useAuth } from '../../../auth/AuthContext';
import { useTeamData } from '../hooks/useTeamData';
import { useMembershipGate } from '../hooks/useMembershipGate';
import { useMemberProfileFields } from '../hooks/useMemberProfileFields';
import { usePendingMembers } from '../hooks/usePendingMembers';
import { useWaitlistMembers } from '../hooks/useWaitlistMembers';
import { usePendingRoleRequests } from '../hooks/usePendingRoleRequests';
import { usePendingMembershipRequests } from '../hooks/usePendingMembershipRequests';
import { useUpcomingEvents } from '../hooks/useUpcomingEvents';
import { mergeCardOrder, placeCardAfter, placeUnpositionedCardAfter, useTeamCardPreferences } from '../hooks/useTeamCardPreferences';
import {
  enabledAtPath,
  getSailingRules,
  isRowingBoatBookingEnabled,
  isRowingBoatMaintenanceEnabled,
  isRowingFleetEnabled,
  isRowingOutingsEnabled,
  isSailingBoatBookingEnabled,
  isSailingBoatMaintenanceEnabled,
  isSailingCrewFinderEnabled,
  isSailingOutingsEnabled,
  withModuleDefaults,
} from '../utils/moduleUtils';
import { isWaitlistJoinRoutingEnabled, isWaitlistModuleEnabled } from '../../../utils/waitlist';
import { getMembershipHandling, isClubManagedMembershipMode } from '../../../utils/membershipHandling';

// Cards (existing files)
import AddressCard from '../detailCards/address';
import AvailabilityCard from '../detailCards/availability';
import SubscriptionsCard from '../detailCards/subscriptions';
import DiscountsCard from '../detailCards/discounts';
import TagsCard from '../detailCards/tags';
import MyTagsCard from '../detailCards/myTags';
import BrandingCard from '../detailCards/brand';
import AdminListCard from '../detailCards/admins';
import AdminPermissionsCard from '../detailCards/adminPermissions';
import MemberListCard from '../detailCards/members';
import ProfileFieldsCard from '../detailCards/profileFields';
import DemographicsCard from '../detailCards/demographics';
import JoinFormCard from '../detailCards/joinForm';
import MetadataCard from '../detailCards/metadata';
import PublicLinksCard from '../detailCards/publicLinks';
import DocumentsCard from '../detailCards/documents';
import StripeCard from '../detailCards/stripeCard';
import AdminActionsCard from '../detailCards/adminActions';
import ManageMembershipCard from '../detailCards/manageMembership';
import MembershipReconciliationCard from '../detailCards/membershipReconciliation';
import EventsCard from '../detailCards/events';
import CoursesCard from '../detailCards/courses';
import ExternalEventAttendeesCard from '../detailCards/externalEventAttendees';
import IncomeDashboardCard from '../detailCards/incomeDashboard';
import XeroIntegrationModal from '../components/modals/XeroIntegrationModal';
import ModulesCard from '../detailCards/modules';
import ReportingCard from '../detailCards/reporting';
import WaitlistCard from '../detailCards/waitlist';
import BoltOnsCard from '../detailCards/boltons';
import BoatBookingCard from '../detailCards/rowing/boatBooking';
import BoatMaintenanceCard from '../detailCards/boatMaintenance';
import MentoringCard from '../detailCards/mentoring';
import RideReportsCard from '../detailCards/rideReports';
import RideDeclarationsCard from '../detailCards/iam/rideDeclarations';
import RowingBoatsCard from '../detailCards/rowing/rowingBoats';
import OutingsCard from '../detailCards/rowing/outings';
import MyOutingsCard from '../detailCards/rowing/myOutings';
import LearnToRowCard from '../detailCards/rowing/learnToRow';
import LearnToSailCard from '../detailCards/sailing/learnToSail';
import DutyRosterCard from '../detailCards/sailing/dutyRoster';
import MooringsCard from '../detailCards/sailing/moorings';
import ClubBoatsCard from '../detailCards/sailing/clubBoats';
import SailingBoatBookingCard from '../detailCards/sailing/boatBooking';
import SailingOutingsCard from '../detailCards/sailing/outings';
import MySailingOutingsCard from '../detailCards/sailing/myOutings';
import SailingCrewFinderCard from '../detailCards/sailing/crewFinder';
import ClubLaunchModal from '../components/modals/ClubLaunchModal';
import EmailBroadcastsCard from '../detailCards/communication/emailBroadcasts';
import InboxBroadcastsCard from '../detailCards/communication/inboxBroadcasts';
import MessageBoardsCard from '../detailCards/communication/messageBoards';
import EmailCampaignDetailsModal from '../components/modals/communication/emailCampaignDetails';
import EmailComposerModal from '../components/modals/communication/emailComposer';
import InboxBroadcastComposerModal from '../components/modals/communication/inboxBroadcastComposer';
import InboxBroadcastDetailsModal from '../components/modals/communication/inboxBroadcastDetails';
import ProfileSearchModal from '../components/modals/ProfileSearchModal';
import TeamEditModal from '../components/modals/TeamEditModal';
import MembershipYearReanchorModal from '../components/modals/MembershipYearReanchorModal';
import BoltOnMembershipYearReanchorModal from '../components/modals/BoltOnMembershipYearReanchorModal';
import TeamCardPreferencesPanel from '../TeamCardPreferencesPanel';
import LoadingModal from '../../ui/LoadingModal';
import PromptModal from '../../ui/PromptModal';
import { getDeletedTagNames, pruneDeletedTagsFromPricingOverrides, removeDeletedTagsFromMembers } from '../utils/tagHelper';
import { resolveLearnToRowConversionStartsAt } from '../utils/learnToRow';
import { RESTRICTABLE_DASHBOARD_CARDS, buildCardRestriction, filterCardsByRestriction } from '../utils/moduleRestrictions';
import { getBespokeModuleSupport, isLegacyTeamMetadata, normalizeTeamMetadata } from '../utils/teamMetadata';
import { buildPermissionState } from '../utils/adminPermissions';
import { getTeamLogoUrl } from '../utils/teamLogo';
import { CREATE_ACCOUNT_LINK_URL, getFunctionUrl } from '../../../utils/functionURLs';
import { clearEmailReplyNotificationsForCampaign } from '../../../utils/emailReplyNotifications';
import { resolveLearnToSailConversionStartsAt } from '../utils/learnToSail';
import { isMooringsMemberVisible } from '../utils/moorings';
import useClubLaunchReadiness from '../hooks/useClubLaunchReadiness';
import useHasAccessibleBoardArchive from '../hooks/useHasAccessibleBoardArchive';
import useStripePaymentGuard from '../../payments/useStripePaymentGuard';
import 'leaflet/dist/leaflet.css';
import '../teams.css';
import './clubHomeCards.css';

const LEGACY_METADATA_PROMPT_KEY_PREFIX = 'memberNest.legacyMetadataPrompt';

function getLegacyMetadataPromptKey(userId, teamId) {
  return `${LEGACY_METADATA_PROMPT_KEY_PREFIX}.${userId}.${teamId}`;
}

function hasDismissedLegacyMetadataPrompt(userId, teamId) {
  if (typeof window === 'undefined' || !userId || !teamId) return false;
  return window.sessionStorage.getItem(getLegacyMetadataPromptKey(userId, teamId)) === '1';
}

function dismissLegacyMetadataPrompt(userId, teamId) {
  if (typeof window === 'undefined' || !userId || !teamId) return;
  window.sessionStorage.setItem(getLegacyMetadataPromptKey(userId, teamId), '1');
}

function stripUndefinedDeep(value) {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) {
    return value.map(stripUndefinedDeep).filter((item) => item !== undefined);
  }
  const isPlainObject = value && typeof value === 'object' && Object.prototype.toString.call(value) === '[object Object]';
  if (!isPlainObject) return value;
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    const cleaned = stripUndefinedDeep(v);
    if (cleaned !== undefined) out[k] = cleaned;
  }
  return out;
}

function buildCardMap(cards = []) {
  return cards.reduce((acc, card) => {
    if (card?.id) acc[card.id] = card;
    return acc;
  }, {});
}

function formatDateTime(value) {
  const date = value?.toDate?.() instanceof Date ? value.toDate() : null;
  if (!date) return '';
  return date.toLocaleString('en-GB', {
    dateStyle: 'full',
    timeStyle: 'short',
  });
}

function withSaveTimeout(promise, timeoutMs = 20000) {
  let timeoutId;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timeoutId = window.setTimeout(
        () => reject(new Error('Saving is taking too long. Check your connection and try again.')),
        timeoutMs
      );
    }),
  ]).finally(() => window.clearTimeout(timeoutId));
}

function timeValue(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function isOpenClubBoardReviewCase(caseEntry = {}) {
  if (String(caseEntry?.status || 'open') !== 'open') return false;
  const clubReview = caseEntry?.clubReview || null;
  return !clubReview || String(clubReview?.status || 'open') === 'open';
}

function sortBoardReviewCases(cases = []) {
  return [...cases].sort((left, right) => timeValue(right?.updatedAt || right?.createdAt) - timeValue(left?.updatedAt || left?.createdAt));
}

export default function TeamDetails({ resolvedTeamId } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeId } = useParams();
  const id = resolvedTeamId || routeId;
  const { user, authLoading } = useAuth();
  const { requireStripePayment, stripeGuardDialog } = useStripePaymentGuard(id);

  // --- Local UI state ---
  const [editingSection, setEditingSection] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerMemberId, setComposerMemberId] = useState('');
  const [inboxBroadcastComposerOpen, setInboxBroadcastComposerOpen] = useState(false);
  const [inboxBroadcastDetailsOpen, setInboxBroadcastDetailsOpen] = useState(false);
  const [selectedInboxBroadcast, setSelectedInboxBroadcast] = useState(null);
  const [inboxBroadcastRefreshKey, setInboxBroadcastRefreshKey] = useState(0);
  const [profileSearchOpen, setProfileSearchOpen] = useState(false);
  const [hasMarkedStripeComplete, setHasMarkedStripeComplete] = useState(false);
  const [clubLaunchOpen, setClubLaunchOpen] = useState(false);
  const [stripeSetupLoading, setStripeSetupLoading] = useState(false);
  const [pendingStripeSetupAfterAddress, setPendingStripeSetupAfterAddress] = useState(false);
  const [pendingSubscriptionsAfterAddress, setPendingSubscriptionsAfterAddress] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [leaveSubmitting, setLeaveSubmitting] = useState(false);
  const [leaveError, setLeaveError] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteBillingAccepted, setDeleteBillingAccepted] = useState(false);
  const [cancelDeleteModalOpen, setCancelDeleteModalOpen] = useState(false);
  const [cancelDeleteSubmitting, setCancelDeleteSubmitting] = useState(false);
  const [cancelDeleteError, setCancelDeleteError] = useState('');
  const [legacyMetadataPromptOpen, setLegacyMetadataPromptOpen] = useState(false);
  const [membershipYearReanchorOpen, setMembershipYearReanchorOpen] = useState(false);
  const [boltOnMembershipYearReanchorOpen, setBoltOnMembershipYearReanchorOpen] = useState(false);
  const [boltOnReanchorAfterMembership, setBoltOnReanchorAfterMembership] = useState(false);
  const [maintenanceTicketRequest, setMaintenanceTicketRequest] = useState(null);
  const [prelaunchCommitteeStatus, setPrelaunchCommitteeStatus] = useState(null);

  const openMaintenanceTicketModal = useCallback((sport, boat) => {
    if (!boat?.id) return;
    setMaintenanceTicketRequest((current) => ({
      requestId: Number(current?.requestId || 0) + 1,
      sport,
      boatId: boat.id,
    }));
  }, []);

  // --- Live team data + profiles ---
  const { team, setTeam, memberProfiles, adminProfiles, loading, error: loadError } = useTeamData(id, 0, user, authLoading);
  const launchMembership = useClubLaunchReadiness(id, clubLaunchOpen);

  useEffect(() => {
    if (!id || !user?.uid || team?.availability?.live === true) {
      setPrelaunchCommitteeStatus(null);
      return;
    }
    let cancelled = false;
    httpsCallable(functions, 'getMyPrelaunchCommitteeStatus')({ teamId: id })
      .then((result) => {
        if (!cancelled) setPrelaunchCommitteeStatus(result.data?.isPrelaunchCommittee ? result.data : null);
      })
      .catch(() => {
        if (!cancelled) setPrelaunchCommitteeStatus(null);
      });
    return () => { cancelled = true; };
  }, [id, team?.availability?.live, user?.uid]);

  // --- Authorisation gate (admin or member) ---
  const { authzChecked } = useMembershipGate({
    id,
    user,
    authLoading,
    team,
  });

  // --- Admin flag ---
  const uid = user?.uid;
  const isAdmin = useMemo(() => Boolean(team?.admins?.includes?.(uid)), [team?.admins, uid]);
  const viewingAsMember = useMemo(() => {
    if (!isAdmin) return false;
    const params = new URLSearchParams(location.search);
    return (
      String(params.get('view') || '')
        .trim()
        .toLowerCase() === 'member'
    );
  }, [isAdmin, location.search]);
  const mentoringInvitationRequested = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return String(params.get('mentoringInvite') || '') === '1';
  }, [location.search]);
  const handleMentoringInvitationOpened = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('mentoringInvite')) return;
    params.delete('mentoringInvite');
    const query = params.toString();
    navigate(query ? `${location.pathname}?${query}` : location.pathname, {
      replace: true,
    });
  }, [location.pathname, location.search, navigate]);
  const rideReportParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return String(params.get('rideReport') || '').trim();
  }, [location.search]);
  const handleRideReportOpened = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('rideReport')) return;
    params.delete('rideReport');
    const query = params.toString();
    navigate(query ? `${location.pathname}?${query}` : location.pathname, {
      replace: true,
    });
  }, [location.pathname, location.search, navigate]);
  const effectiveIsAdmin = isAdmin && !viewingAsMember;
  const permissions = useMemo(
    () =>
      buildPermissionState({
        isFullAdmin: effectiveIsAdmin,
        assignment: viewingAsMember ? null : team?.myAdminAssignment,
      }),
    [effectiveIsAdmin, team?.myAdminAssignment, viewingAsMember]
  );
  const hasAdminAccess = permissions.isFullAdmin || permissions.isLimitedAdmin;
  const bespokeModuleSupport = useMemo(
    () => getBespokeModuleSupport(team?.teamMetadata || {}),
    [team?.teamMetadata]
  );
  const supportsIamRoadSmart = bespokeModuleSupport.iamRoadSmart;
  const supportsRowing = bespokeModuleSupport.rowing;
  const supportsSailing = bespokeModuleSupport.sailing;
  const canManageSettings = permissions.has('settings.manage');
  const canManageMembers = permissions.has('members.manage');
  const canExportMembers = permissions.has('members.export');
  const canManageMembership = permissions.has('membership.manage');
  const canManageFinance = permissions.has('finance.manage');
  const [xeroRecoveryOpen, setXeroRecoveryOpen] = useState(false);
  useEffect(() => {
    if (!authzChecked) return;
    const params = new URLSearchParams(location.search);
    if (params.get('integration') !== 'xero') return;
    if (canManageFinance) setXeroRecoveryOpen(true);
    params.delete('integration');
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: true });
  }, [authzChecked, canManageFinance, location.pathname, location.search, navigate]);
  const canManageReports = permissions.has('reports.manage');
  const canManageEvents = permissions.has('events.manage');
  const canManageCourses = permissions.has('courses.manage');
  const canManageBookingForms = permissions.has('bookingForms.manage');
  const canCheckInEvents = canManageEvents || permissions.has('eventCheckIn.manage');
  const canManageCommunications = permissions.has('communications.manage');
  const canManageDocuments = permissions.has('documents.manage');
  const canManageMentoring = permissions.has('mentoring.manage');
  const canManageRideReports = permissions.has('rideReports.manage');
  const canManageRideDeclarations = permissions.has('rideDeclarations.manage');
  const canManageRowing = permissions.has('rowing.manage');
  const canManageRowingFleet = permissions.has('rowingFleet.manage');
  const canManageRowingBoatBooking = permissions.has('rowingBoatBooking.manage');
  const canManageRowingOutings = permissions.has('rowingOutings.manage');
  const canManageRowingBoatMaintenance = permissions.has('rowingBoatMaintenance.manage');
  const canManageSailing = permissions.has('sailing.manage');
  const canManageMoorings = permissions.has('moorings.manage');
  const canManageSailingFleet = permissions.has('sailingFleet.manage');
  const canManageSailingBoatBooking = permissions.has('sailingBoatBooking.manage');
  const canManageSailingOutings = permissions.has('sailingOutings.manage');
  const canManageSailingCrewFinder = permissions.has('sailingCrewFinder.manage');
  const canManageSailingBoatMaintenance = permissions.has('sailingBoatMaintenance.manage');
  useEffect(() => {
    if (!authzChecked) return;
    const params = new URLSearchParams(location.search);
    if (params.get('resources') !== '1') return;
    const courseType = params.get('courseType') || '';
    const courseId = params.get('course') || '';
    const allowed = courseType === 'generic'
      ? (canManageCourses || canManageRowingBoatBooking || canManageSailingBoatBooking)
      : courseType === 'learn_to_row'
        ? (canManageRowing || canManageRowingBoatBooking)
        : courseType === 'learn_to_sail'
          ? (canManageSailing || canManageSailingBoatBooking)
          : false;
    if (!courseId || !allowed) return;
    setEditingSection('courseResources');
    setEditingData({ courseType, courseId });
  }, [authzChecked, canManageCourses, canManageRowing, canManageRowingBoatBooking, canManageSailing, canManageSailingBoatBooking, location.search]);
  useEffect(() => {
    if (!authzChecked) return;
    const params = new URLSearchParams(location.search);
    const action = params.get('resumeStripeAction');
    const resumeEntity = String(params.get('resumeEntity') || '').trim();
    const targets = {
      bolt_ons: { permission: canManageMembership, section: 'boltOns', data: null, label: 'Bolt-ons' },
      generic_courses: { permission: canManageCourses, section: 'courses', data: resumeEntity === 'new' ? { createNew: true, canManageBookingForms } : { focusCourseId: resumeEntity, canManageBookingForms }, label: 'Paid courses' },
      learn_to_row: { permission: canManageRowing && supportsRowing && team?.modules?.rowing?.learnToRow?.enabled === true, section: 'learnToRow', data: resumeEntity === 'general' ? { initialPanel: 'general' } : resumeEntity === 'new' ? { createNew: true } : { focusCourseId: resumeEntity }, label: 'Paid Learn to Row signup' },
      learn_to_sail: { permission: canManageSailing && supportsSailing && team?.modules?.sailing?.learnToSail?.enabled === true, section: 'learnToSail', data: resumeEntity === 'general' ? { initialPanel: 'general' } : resumeEntity === 'new' ? { createNew: true } : { focusCourseId: resumeEntity }, label: 'Paid Learn to Sail signup' },
      moorings_payments: { permission: canManageMoorings && supportsSailing && team?.modules?.sailing?.moorings?.enabled === true, section: 'moorings', data: { initialTab: 'payments' }, label: 'Mooring payments' },
      sailing_boat_payments: { permission: canManageSailingFleet && supportsSailing && team?.modules?.sailing?.boats?.enabled === true, section: 'sailingBoats', data: null, label: 'Paid sailing boat bookings' },
    };
    const target = targets[action];
    if (!target?.permission) return;
    params.delete('resumeStripeAction');
    params.delete('resumeEntity');
    const query = params.toString();
    navigate(query ? `${location.pathname}?${query}` : location.pathname, { replace: true });
    requireStripePayment({
      feature: action,
      featureLabel: target.label,
      returnTarget: action,
      onReady: () => {
        setEditingSection(target.section);
        setEditingData(target.data);
      },
    });
  }, [authzChecked, canManageBookingForms, canManageCourses, canManageMembership, canManageMoorings, canManageRowing, canManageSailing, canManageSailingFleet, location.pathname, location.search, navigate, requireStripePayment, supportsRowing, supportsSailing, team?.modules?.rowing?.learnToRow?.enabled, team?.modules?.sailing?.boats?.enabled, team?.modules?.sailing?.learnToSail?.enabled, team?.modules?.sailing?.moorings?.enabled]);
  const emailCampaignParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return String(params.get('emailCampaign') || '').trim();
  }, [location.search]);
  const closeEmailCampaignModal = useCallback(() => {
    setEmailModalOpen(false);
    setSelectedCampaign(null);
    if (!emailCampaignParam) return;
    const params = new URLSearchParams(location.search);
    params.delete('emailCampaign');
    const query = params.toString();
    navigate(query ? `${location.pathname}?${query}` : location.pathname, {
      replace: true,
    });
  }, [emailCampaignParam, location.pathname, location.search, navigate]);
  const memberPreviewLink = useMemo(() => {
    const params = new URLSearchParams(location.search);
    params.set('view', 'member');
    const query = params.toString();
    return query ? `${location.pathname}?${query}` : location.pathname;
  }, [location.pathname, location.search]);
  const autoOpenMembershipManager = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (
      String(params.get('membership') || '')
        .trim()
        .toLowerCase() === 'manage'
    );
  }, [location.search]);
  const handleMembershipAutoOpenHandled = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('membership')) return;
    params.delete('membership');
    const query = params.toString();
    navigate(query ? `${location.pathname}?${query}` : location.pathname, {
      replace: true,
    });
  }, [location.pathname, location.search, navigate]);
  useEffect(() => {
    if (!emailCampaignParam || !id || !canManageCommunications) return undefined;
    let cancelled = false;
    getDoc(doc(db, 'teams', id, 'emailCampaigns', emailCampaignParam))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        setSelectedCampaign({ id: snap.id, ...snap.data() });
        setEmailModalOpen(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [canManageCommunications, emailCampaignParam, id]);
  useEffect(() => {
    if (!emailModalOpen || !selectedCampaign?.id || !id || !user?.uid || !canManageCommunications) return;
    clearEmailReplyNotificationsForCampaign({
      db,
      userId: user.uid,
      teamId: id,
      campaignId: selectedCampaign.id,
    }).catch(() => {});
  }, [canManageCommunications, emailModalOpen, id, selectedCampaign?.id, user?.uid]);
  const adminViewLink = useMemo(() => {
    const params = new URLSearchParams(location.search);
    params.delete('view');
    const query = params.toString();
    return query ? `${location.pathname}?${query}` : location.pathname;
  }, [location.pathname, location.search]);
  const joinFormDemoLink = `/join/${id}?demo=1`;

  // --- Other live collections (pending + events) ---
  const pendingMembers = usePendingMembers(id, authzChecked && canManageMembership);
  const waitlistMembers = useWaitlistMembers(id, authzChecked && canManageMembership);
  const [learnToRowApprovalMembers, setLearnToRowApprovalMembers] = useState([]);
  const [learnToSailApprovalMembers, setLearnToSailApprovalMembers] = useState([]);
  const { pendingMembershipRequests, loading: membershipRequestsLoading } = usePendingMembershipRequests(id, authzChecked && canManageMembership);
  const { pendingRoleRequests, loading: roleRequestsLoading } = usePendingRoleRequests(id, authzChecked && canManageEvents);
  const upcomingEvents = useUpcomingEvents(id, authzChecked, canManageEvents || canCheckInEvents);
  const { profileFields: myProfileFields, profileUpdateRequestOpen, profileUpdateRequestedAt } = useMemberProfileFields(id, user?.uid);
  const { preferences: cardPreferences, loading: cardPreferencesLoading, saving: cardPreferencesSaving, setCardHidden, showAllCards, moveCard, resetPreferences } = useTeamCardPreferences(id);
  const modulesWithDefaults = useMemo(() => withModuleDefaults(team?.modules || {}), [team?.modules]);
  const savedMembershipMode = getMembershipHandling(team || {}).mode;
  const membershipReconciliationEnabled = modulesWithDefaults.membershipReconciliation?.enabled === true;
  const sailingCrewFinderEnabled = supportsSailing && isSailingCrewFinderEnabled(modulesWithDefaults);
  const boardsEnabled = enabledAtPath(modulesWithDefaults, ['communications', 'boards']);
  const mooringsMemberVisible = isMooringsMemberVisible(modulesWithDefaults);
  const eventCommentsEnabled = team?.eventSettings?.commentsEnabled === true;
  const hasBoardArchive = useHasAccessibleBoardArchive(id, authzChecked && !boardsEnabled && !eventCommentsEnabled);
  const boardSurfaceAvailable = boardsEnabled || eventCommentsEnabled || hasBoardArchive;
  const [openBoardReviewCases, setOpenBoardReviewCases] = useState([]);
  const [boardReviewCasesLoading, setBoardReviewCasesLoading] = useState(false);

  useEffect(() => {
    if (!authzChecked || !uid || !supportsSailing || !modulesWithDefaults?.sailing?.dutyRoster?.enabled) return;
    const params = new URLSearchParams(location.search);
    const panel = String(params.get('panel') || '')
      .trim()
      .toLowerCase();
    if (panel !== 'dutyroster') return;
    const requestedTab = String(params.get('tab') || 'mine')
      .trim()
      .toLowerCase();
    const allowedTabs = canManageSailing ? new Set(['overview', 'grid', 'templates', 'draft', 'mine', 'swaps', 'settings']) : new Set(['overview', 'mine', 'swaps']);
    const initialTab = allowedTabs.has(requestedTab) ? requestedTab : 'mine';
    setEditingSection('dutyRoster');
    setEditingData({
      initialTab,
      isAdmin: canManageSailing,
    });
    params.delete('panel');
    params.delete('tab');
    const query = params.toString();
    navigate(query ? `${location.pathname}?${query}` : location.pathname, {
      replace: true,
    });
  }, [authzChecked, canManageSailing, location.pathname, location.search, modulesWithDefaults?.sailing?.dutyRoster?.enabled, navigate, supportsSailing, uid]);

  useEffect(() => {
    if (!authzChecked || !uid || !sailingCrewFinderEnabled) return;
    const params = new URLSearchParams(location.search);
    const panel = String(params.get('panel') || '')
      .trim()
      .toLowerCase();
    if (panel !== 'sailingcrewfinder' && panel !== 'crewfinder') return;
    const requestedTab = String(params.get('tab') || 'crew_needed')
      .trim()
      .toLowerCase();
    const allowedTabs = canManageSailingCrewFinder ? new Set(['crew_needed', 'available_to_crew', 'my_posts', 'admin']) : new Set(['crew_needed', 'available_to_crew', 'my_posts']);
    const initialTab = allowedTabs.has(requestedTab) ? requestedTab : 'crew_needed';
    setEditingSection('sailingCrewFinder');
    setEditingData({
      initialTab,
      isAdmin: canManageSailingCrewFinder,
    });
    params.delete('panel');
    params.delete('tab');
    params.delete('post');
    const query = params.toString();
    navigate(query ? `${location.pathname}?${query}` : location.pathname, {
      replace: true,
    });
  }, [authzChecked, canManageSailingCrewFinder, location.pathname, location.search, navigate, sailingCrewFinderEnabled, uid]);

  // --- Brand theming (safe before early returns) ---
  const brandForTheme = team?.brand || {};
  const primaryColor = brandForTheme.primaryColor;
  const secondaryColor = brandForTheme.secondaryColor;
  const headingTextColor = brandForTheme.headingTextColor;
  const bodyTextColor = brandForTheme.bodyTextColor;

  // Apply team brand to the page background (body) while this view is mounted.
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const prev = {
      primary: root.style.getPropertyValue('--team-primary'),
      secondary: root.style.getPropertyValue('--team-secondary'),
      heading: root.style.getPropertyValue('--team-heading-text'),
      body: root.style.getPropertyValue('--team-body-text'),
    };
    if (primaryColor) root.style.setProperty('--team-primary', primaryColor);
    if (secondaryColor) root.style.setProperty('--team-secondary', secondaryColor);
    if (headingTextColor) root.style.setProperty('--team-heading-text', headingTextColor);
    if (bodyTextColor) root.style.setProperty('--team-body-text', bodyTextColor);
    body.classList.add('team-themed');
    return () => {
      body.classList.remove('team-themed');
      const restore = (key, value) => {
        if (value) root.style.setProperty(key, value);
        else root.style.removeProperty(key);
      };
      restore('--team-primary', prev.primary);
      restore('--team-secondary', prev.secondary);
      restore('--team-heading-text', prev.heading);
      restore('--team-body-text', prev.body);
    };
  }, [primaryColor, secondaryColor, headingTextColor, bodyTextColor]);
  useEffect(() => {
    if (!authzChecked || !canManageMembership) return;
    const params = new URLSearchParams(location.search);
    const panel = String(params.get('panel') || '').trim();
    const action = String(params.get('action') || '')
      .trim()
      .toLowerCase();
    if (panel !== 'pendingMembers' && action !== 'pending-member') return;
    setEditingSection('pendingMembers');
    setEditingData(pendingMembers || []);
    navigate(location.pathname, {
      replace: true,
    });
  }, [authzChecked, canManageMembership, location.pathname, location.search, navigate, pendingMembers]);
  useEffect(() => {
    if (!authzChecked || !canManageMembers) return;
    const params = new URLSearchParams(location.search);
    const panel = String(params.get('panel') || '')
      .trim()
      .toLowerCase();
    const requestType = String(params.get('request') || '')
      .trim()
      .toLowerCase();
    const memberId = String(params.get('member') || '').trim();
    if (panel !== 'members' && requestType !== 'membership') return;
    if (!memberId) return;
    const member = memberProfiles.find((entry) => entry.id === memberId);
    if (!member) return;
    setEditingSection('member');
    setEditingData(member);
    navigate(location.pathname, {
      replace: true,
    });
  }, [authzChecked, canManageMembers, location.pathname, location.search, memberProfiles, navigate]);
  useEffect(() => {
    if (!authzChecked || !uid) return;
    const params = new URLSearchParams(location.search);
    const panel = String(params.get('panel') || '')
      .trim()
      .toLowerCase();
    if (panel !== 'profiledetails' && panel !== 'qualifications') return;
    if (!modulesWithDefaults.profile?.enabled || team?.profileFieldsConfig?.enabled === false || !Array.isArray(team?.profileFieldsConfig?.fields) || team.profileFieldsConfig.fields.length === 0) {
      return;
    }
    setEditingSection('memberProfileFields');
    setEditingData(null);
    params.delete('panel');
    const query = params.toString();
    navigate(query ? `${location.pathname}?${query}` : location.pathname, {
      replace: true,
    });
  }, [authzChecked, location.pathname, location.search, modulesWithDefaults.profile?.enabled, navigate, team?.profileFieldsConfig, uid]);
  useEffect(() => {
    if (!authzChecked || !canManageRowing || !supportsRowing || team?.modules?.rowing?.learnToRow?.enabled !== true) return;
    const params = new URLSearchParams(location.search);
    const panel = String(params.get('panel') || '')
      .trim()
      .toLowerCase();
    if (panel !== 'learntorowwaitlist') return;
    setEditingSection('learnToRow');
    setEditingData({ initialPanel: 'waitlist' });
    navigate(location.pathname, {
      replace: true,
    });
  }, [authzChecked, canManageRowing, location.pathname, location.search, navigate, supportsRowing, team?.modules?.rowing?.learnToRow?.enabled]);
  useEffect(() => {
    if (!authzChecked || !canManageSailing || !supportsSailing || team?.modules?.sailing?.learnToSail?.enabled !== true) return;
    const params = new URLSearchParams(location.search);
    const panel = String(params.get('panel') || '')
      .trim()
      .toLowerCase();
    if (panel !== 'learntosailwaitlist') return;
    setEditingSection('learnToSail');
    setEditingData({ initialPanel: 'waitlist' });
    navigate(location.pathname, {
      replace: true,
    });
  }, [authzChecked, canManageSailing, location.pathname, location.search, navigate, supportsSailing, team?.modules?.sailing?.learnToSail?.enabled]);
  useEffect(() => {
    const teamMetadata = team?.teamMetadata || {};
    if (!canManageSettings || !id || !user?.uid || !team || !isLegacyTeamMetadata(teamMetadata)) {
      setLegacyMetadataPromptOpen(false);
      return;
    }
    if (editingSection) return;
    if (hasDismissedLegacyMetadataPrompt(user.uid, id)) return;
    setLegacyMetadataPromptOpen(true);
  }, [canManageSettings, editingSection, id, team, user?.uid]);

  const handleCloseLegacyMetadataPrompt = useCallback(() => {
    dismissLegacyMetadataPrompt(user?.uid, id);
    setLegacyMetadataPromptOpen(false);
  }, [id, user?.uid]);

  const handleOpenLegacyMetadataEditor = useCallback(() => {
    dismissLegacyMetadataPrompt(user?.uid, id);
    setLegacyMetadataPromptOpen(false);
    setEditingSection('teamMetadata');
    setEditingData(team?.teamMetadata || {});
  }, [id, team?.teamMetadata, user?.uid]);

  useEffect(() => {
    if (!id || !authzChecked || !canManageRowing || !supportsRowing || team?.modules?.rowing?.learnToRow?.enabled !== true) {
      setLearnToRowApprovalMembers([]);
      return undefined;
    }
    let requestVersion = 0;
    const approvalsQuery = query(collection(db, 'teams', id, 'learnToRowWaitlist'), where('status', '==', 'awaiting_approval'));
    const unsubscribe = safeOnSnapshot(approvalsQuery, async (snapshot) => {
      const currentVersion = ++requestVersion;
      const entries = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      if (entries.length === 0) {
        setLearnToRowApprovalMembers([]);
        return;
      }
      const uniqueUserIds = Array.from(
        new Set(
          entries
            .flatMap((entry) => [entry?.id, entry?.userId, entry?.payerUserId])
            .map((value) => (typeof value === 'string' ? value.trim() : ''))
            .filter(Boolean)
        )
      );
      try {
        const userSnapshots = await Promise.all(
          uniqueUserIds.map(async (uid) => {
            const snap = await getDoc(doc(db, 'users', uid)).catch(() => null);
            return [uid, snap?.exists() ? snap.data() || {} : null];
          })
        );
        if (currentVersion !== requestVersion) return;
        const usersById = Object.fromEntries(userSnapshots);
        setLearnToRowApprovalMembers(
          entries.map((entry) => {
            const primaryProfile = usersById[entry.userId] || usersById[entry.id] || null;
            const payerProfile = usersById[entry.payerUserId] || null;
            return {
              ...entry,
              ...(primaryProfile || {}),
              payerDisplayName: payerProfile?.displayName || [payerProfile?.firstName, payerProfile?.lastName].filter(Boolean).join(' ').trim() || payerProfile?.username || entry.payerUserId || '',
            };
          })
        );
      } catch {
        if (currentVersion !== requestVersion) return;
        setLearnToRowApprovalMembers(entries);
      }
    });
    return () => unsubscribe();
  }, [authzChecked, canManageRowing, id, supportsRowing, team?.modules?.rowing?.learnToRow?.enabled]);
  useEffect(() => {
    if (!id || !authzChecked || !canManageSailing || !supportsSailing || team?.modules?.sailing?.learnToSail?.enabled !== true) {
      setLearnToSailApprovalMembers([]);
      return undefined;
    }
    let requestVersion = 0;
    const approvalsQuery = query(collection(db, 'teams', id, 'learnToSailWaitlist'), where('status', '==', 'awaiting_approval'));
    const unsubscribe = safeOnSnapshot(approvalsQuery, async (snapshot) => {
      const currentVersion = ++requestVersion;
      const entries = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      if (entries.length === 0) {
        setLearnToSailApprovalMembers([]);
        return;
      }
      const uniqueUserIds = Array.from(
        new Set(
          entries
            .flatMap((entry) => [entry?.id, entry?.userId, entry?.payerUserId])
            .map((value) => (typeof value === 'string' ? value.trim() : ''))
            .filter(Boolean)
        )
      );
      try {
        const userSnapshots = await Promise.all(
          uniqueUserIds.map(async (userId) => {
            const snap = await getDoc(doc(db, 'users', userId)).catch(() => null);
            return [userId, snap?.exists() ? snap.data() || {} : null];
          })
        );
        if (currentVersion !== requestVersion) return;
        const usersById = Object.fromEntries(userSnapshots);
        setLearnToSailApprovalMembers(
          entries.map((entry) => {
            const primaryProfile = usersById[entry.userId] || usersById[entry.id] || null;
            const payerProfile = usersById[entry.payerUserId] || null;
            return {
              ...entry,
              ...(primaryProfile || {}),
              payerDisplayName: payerProfile?.displayName || [payerProfile?.firstName, payerProfile?.lastName].filter(Boolean).join(' ').trim() || payerProfile?.username || '',
            };
          })
        );
      } catch {
        if (currentVersion !== requestVersion) return;
        setLearnToSailApprovalMembers(entries);
      }
    });
    return () => unsubscribe();
  }, [authzChecked, canManageSailing, id, supportsSailing, team?.modules?.sailing?.learnToSail?.enabled]);
  useEffect(() => {
    if (!id || !authzChecked || !canManageCommunications || !boardSurfaceAvailable) {
      setOpenBoardReviewCases([]);
      setBoardReviewCasesLoading(false);
      return undefined;
    }

    setBoardReviewCasesLoading(true);
    const unsubscribe = safeOnSnapshot(
      collection(db, 'teams', id, 'boardModerationCases'),
      (snap) => {
        setOpenBoardReviewCases(
          sortBoardReviewCases(
            snap.docs
              .map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data(),
              }))
              .filter(isOpenClubBoardReviewCase)
          )
        );
        setBoardReviewCasesLoading(false);
      },
      () => {
        setBoardReviewCasesLoading(false);
      },
      { label: 'team-board-review-cases', teamId: id }
    );

    return () => unsubscribe();
  }, [authzChecked, boardSurfaceAvailable, canManageCommunications, id]);

  // --- Early returns ---
  if (authLoading) return <p className="text-muted">Loading authentication...</p>;
  if (!user) {
    const nextPath = `${location.pathname}${location.search || ''}`;
    return <Navigate to={`/login?next=${encodeURIComponent(nextPath)}`} replace />;
  }
  if (loading) {
    return (
      <div className="cards-container">
        {Array.from({
          length: 6,
        }).map((_, i) => (
          <div className="card skeleton" key={i}>
            <div className="skeleton-header" />
            <div className="skeleton-body" />
          </div>
        ))}
      </div>
    );
  }
  if (loadError) return <p className="text-muted">Error: {String(loadError)}</p>;
  if (!team) return null;
  if (!authzChecked) return <p className="text-muted">Checking membership access...</p>;

  // --- Unpack team ---
  const {
    name,
    description,
    address = {},
    subscriptions = {},
    tags = [],
    brand = {},
    checklist = {},
    availability = {},
    joinFormConfig = {},
    teamMetadata = {},
    publicLinks = [],
    documents = [],
    modules = {},
    rowing = {},
  } = team;
  const myMemberProfile = memberProfiles.find((member) => member.id === uid) || null;
  const boatMaintenanceSports = [supportsRowing ? 'rowing' : null, supportsSailing ? 'sailing' : null].filter(Boolean);
  const hasConfiguredTags = Array.isArray(tags) && tags.some((tag) => tag?.type !== 'ageRule');
  const myAssignedTagCount = (() => {
    const rawTags = myMemberProfile?.tags;
    if (Array.isArray(rawTags)) return rawTags.filter(Boolean).length;
    if (rawTags && typeof rawTags === 'object') {
      return Object.values(rawTags).filter(Boolean).length;
    }
    return 0;
  })();
  const learnToRowState = myMemberProfile?.learnToRow || null;
  const learnToSailState = myMemberProfile?.learnToSail || null;
  const genericCourseState = myMemberProfile?.courseEnrollment || null;
  const genericCourseStatus = String(genericCourseState?.status || '')
    .trim()
    .toLowerCase();
  const learnToRowConversionStart = (() => {
    const date = resolveLearnToRowConversionStartsAt(learnToRowState || {});
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  })();
  const learnToRowStatus = String(learnToRowState?.status || '')
    .trim()
    .toLowerCase();
  const learnToSailConversionStart = (() => {
    const date = resolveLearnToSailConversionStartsAt(learnToSailState || {});
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  })();
  const learnToSailStatus = String(learnToSailState?.status || '')
    .trim()
    .toLowerCase();
  const activeCourseRestrictionSource = (() => {
    if (genericCourseStatus === 'active_course' && genericCourseState?.restrictActiveCourseCards === true) {
      return genericCourseState;
    }
    if (learnToRowStatus === 'active_course' && modulesWithDefaults?.rowing?.learnToRow?.restrictActiveCourseCards === true) {
      return modulesWithDefaults.rowing.learnToRow;
    }
    if (learnToSailStatus === 'active_course' && modulesWithDefaults?.sailing?.learnToSail?.restrictActiveCourseCards === true) {
      return modulesWithDefaults.sailing.learnToSail;
    }
    return null;
  })();
  const activeMemberCardRestriction =
    !hasAdminAccess && activeCourseRestrictionSource
      ? buildCardRestriction({
          enabled: true,
          allowedCardIds: Array.isArray(activeCourseRestrictionSource?.activeCourseVisibleCardIds)
            ? activeCourseRestrictionSource.activeCourseVisibleCardIds
            : RESTRICTABLE_DASHBOARD_CARDS.map((card) => card.id),
          alwaysAllowedCardIds: activeCourseRestrictionSource === genericCourseState ? ['manageMembership', 'courses'] : ['manageMembership'],
          reason: 'course_access',
        })
      : null;
  const learnToRowGateActive =
    !effectiveIsAdmin &&
    Boolean(learnToRowState) &&
    (learnToRowStatus === 'awaiting_conversion' || (learnToRowStatus === 'active_course' && learnToRowConversionStart instanceof Date && learnToRowConversionStart.getTime() <= Date.now()));
  const learnToSailGateActive =
    !effectiveIsAdmin &&
    Boolean(learnToSailState) &&
    (learnToSailStatus === 'awaiting_conversion' || (learnToSailStatus === 'active_course' && learnToSailConversionStart instanceof Date && learnToSailConversionStart.getTime() <= Date.now()));
  const genericCourseConversionStart = genericCourseState?.conversionStartsAt?.toDate?.() || (genericCourseState?.conversionStartsAt ? new Date(genericCourseState.conversionStartsAt) : null);
  const genericCourseGateActive =
    !effectiveIsAdmin &&
    Boolean(genericCourseState) &&
    (genericCourseStatus === 'awaiting_conversion' ||
      (genericCourseStatus === 'active_course' &&
        genericCourseConversionStart instanceof Date &&
        !Number.isNaN(genericCourseConversionStart.getTime()) &&
        genericCourseConversionStart.getTime() <= Date.now()));
  const learnToRowPurgeDateLabel = (() => {
    const date = learnToRowState?.purgeAt?.toDate?.() instanceof Date ? learnToRowState.purgeAt.toDate() : learnToRowState?.purgeAt ? new Date(learnToRowState.purgeAt) : null;
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-GB', {
      dateStyle: 'full',
      timeStyle: 'short',
    });
  })();
  const learnToSailPurgeDateLabel = (() => {
    const date = learnToSailState?.purgeAt?.toDate?.() instanceof Date ? learnToSailState.purgeAt.toDate() : learnToSailState?.purgeAt ? new Date(learnToSailState.purgeAt) : null;
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-GB', {
      dateStyle: 'full',
      timeStyle: 'short',
    });
  })();
  const genericCoursePurgeDate = genericCourseState?.purgeAt?.toDate?.() || (genericCourseState?.purgeAt ? new Date(genericCourseState.purgeAt) : null);
  const genericCoursePurgeDateLabel =
    genericCoursePurgeDate instanceof Date && !Number.isNaN(genericCoursePurgeDate.getTime())
      ? genericCoursePurgeDate.toLocaleString('en-GB', {
          dateStyle: 'full',
          timeStyle: 'short',
        })
      : '';
  const courseGateActive = genericCourseGateActive || learnToRowGateActive || learnToSailGateActive;
  const courseGateLabel = genericCourseGateActive ? genericCourseState?.courseName || 'Course' : learnToSailGateActive ? 'Learn to Sail' : 'Learn to Row';
  const courseGatePurgeDateLabel = genericCourseGateActive ? genericCoursePurgeDateLabel : learnToSailGateActive ? learnToSailPurgeDateLabel : learnToRowPurgeDateLabel;
  const showMyTagsCard = !effectiveIsAdmin && (hasConfiguredTags || myAssignedTagCount > 0);
  const messagingEnabled = enabledAtPath(modulesWithDefaults, ['communications', 'messages']);
  const emailEnabled = enabledAtPath(modulesWithDefaults, ['communications', 'email']);
  const canUseMemberDirectory = canManageMembers || canExportMembers || ((messagingEnabled || emailEnabled) && canManageCommunications);
  const inboxBroadcastsEnabled = enabledAtPath(modulesWithDefaults, ['communications', 'inboxBroadcasts']);
  const waitlistModuleEnabled = isWaitlistModuleEnabled(modulesWithDefaults);
  const waitlistJoinRoutingEnabled = isWaitlistJoinRoutingEnabled(modulesWithDefaults);
  const adminProfileSearchEnabled = Boolean(canManageMembers);
  const profileFieldsEditable = Boolean(
    canManageMembers &&
    modulesWithDefaults.profile?.enabled &&
    team?.profileFieldsConfig?.enabled !== false &&
    Array.isArray(team?.profileFieldsConfig?.fields) &&
    team.profileFieldsConfig.fields.length > 0
  );
  const inboxBroadcastsAvailable = Boolean(messagingEnabled && inboxBroadcastsEnabled);
  const profileCardForcedVisible = Boolean(profileUpdateRequestOpen);
  const pendingDeletionExecuteAfter = team?.pendingDeletionExecuteAfter || null;
  const pendingDeletionRequestedByName = String(team?.pendingDeletionRequestedByName || '').trim();
  const pendingDeletionInProgressAt = team?.pendingDeletionInProgressAt || null;
  const deletionPending = Boolean(pendingDeletionExecuteAfter);
  const adminCount = Array.isArray(team?.admins) ? team.admins.filter(Boolean).length : 0;
  const deleteIsImmediate = availability?.live !== true || adminCount <= 1;
  const requiresManagedClosureSettlement =
    availability?.live === true && isClubManagedMembershipMode(savedMembershipMode);
  const adminActionCount =
    (pendingMembers?.length || 0) +
    (waitlistMembers?.length || 0) +
    (learnToRowApprovalMembers?.length || 0) +
    (learnToSailApprovalMembers?.length || 0) +
    (pendingMembershipRequests?.length || 0) +
    (pendingRoleRequests?.length || 0) +
    (deletionPending && effectiveIsAdmin ? 1 : 0);
  const canShowAdminActionsCard = hasAdminAccess;
  const shouldForceShowAdminActionsCard = canShowAdminActionsCard && adminActionCount > 0;
  const openBoardReviewCaseCount = openBoardReviewCases.length;
  const teamLogoUrl = getTeamLogoUrl(team);
  const normalizedHeaderMetadata = normalizeTeamMetadata(teamMetadata || {});
  const headerSubtitle = description || normalizedHeaderMetadata.primaryLabel || teamMetadata?.ageGroup || '';

  // --- Handlers ---
  const openLeaveModal = () => {
    setLeaveError('');
    setLeaveModalOpen(true);
  };
  const closeLeaveModal = () => {
    if (leaveSubmitting) return;
    setLeaveModalOpen(false);
    setLeaveError('');
  };
  const canDeleteTeam = effectiveIsAdmin && !deletionPending;
  const canCancelDeleteTeam = effectiveIsAdmin && deletionPending && !pendingDeletionInProgressAt;
  const openDeleteModal = () => {
    setDeleteError('');
    setDeleteConfirmName('');
    setDeleteBillingAccepted(false);
    setDeleteModalOpen(true);
  };
  const closeDeleteModal = () => {
    if (deleteSubmitting) return;
    setDeleteModalOpen(false);
    setDeleteError('');
    setDeleteConfirmName('');
    setDeleteBillingAccepted(false);
  };
  const openCancelDeleteModal = () => {
    setCancelDeleteError('');
    setCancelDeleteModalOpen(true);
  };
  const closeCancelDeleteModal = () => {
    if (cancelDeleteSubmitting) return;
    setCancelDeleteModalOpen(false);
    setCancelDeleteError('');
  };
  const handleLeaveTeam = async () => {
    if (!id) {
      setLeaveError('Missing team identifier. Refresh and try again.');
      return;
    }
    if (!user?.uid) {
      setLeaveError('You must be signed in to leave this club.');
      return;
    }
    setLeaveSubmitting(true);
    setLeaveError('');
    try {
      const leaveTeamFn = httpsCallable(functions, 'leaveTeamCallable');
      await leaveTeamFn({
        teamId: id,
        userId: user.uid,
      });
      setLeaveModalOpen(false);
      alert('You have left the club.');
      navigate('/teams');
    } catch (err) {
      const message = err?.message || 'Failed to leave the club. Please try again.';
      setLeaveError(message);
    } finally {
      setLeaveSubmitting(false);
    }
  };
  const handleDeleteTeam = async () => {
    if (!id) {
      setDeleteError('Missing team identifier. Refresh and try again.');
      return;
    }
    if (!user?.uid) {
      setDeleteError('You must be signed in to delete this club.');
      return;
    }
    if (!String(name || '').trim()) {
      setDeleteError('Missing club name. Refresh and try again.');
      return;
    }
    setDeleteSubmitting(true);
    setDeleteError('');
    try {
      const deleteTeamFn = httpsCallable(functions, 'deleteTeam');
      await deleteTeamFn({
        teamId: id,
        confirmTeamName: deleteConfirmName,
        settleManagedBillingAccepted: requiresManagedClosureSettlement
          ? deleteBillingAccepted
          : false,
      });
      setDeleteModalOpen(false);
      setDeleteConfirmName('');
      setDeleteBillingAccepted(false);
      if (deleteIsImmediate) {
        navigate('/teams');
      }
    } catch (err) {
      setDeleteError(err?.message || `Failed to ${deleteIsImmediate ? 'delete' : 'schedule deletion for'} the club. Please try again.`);
    } finally {
      setDeleteSubmitting(false);
    }
  };
  const handleCancelDeleteTeam = async () => {
    if (!id) {
      setCancelDeleteError('Missing team identifier. Refresh and try again.');
      return;
    }
    if (!user?.uid) {
      setCancelDeleteError('You must be signed in to cancel deletion.');
      return;
    }
    setCancelDeleteSubmitting(true);
    setCancelDeleteError('');
    try {
      const cancelDeleteTeamFn = httpsCallable(functions, 'cancelTeamDeletion');
      await cancelDeleteTeamFn({ teamId: id });
      setCancelDeleteModalOpen(false);
    } catch (err) {
      setCancelDeleteError(err?.message || 'Failed to cancel deletion. Please try again.');
    } finally {
      setCancelDeleteSubmitting(false);
    }
  };
  const handleStripeSetup = async ({ skipAddressCheck = false } = {}) => {
    if (!user || !id || stripeSetupLoading) return;
    if (!skipAddressCheck) {
      const teamCountry = String(team?.address?.country || '').trim();
      if (!teamCountry) {
        setPendingStripeSetupAfterAddress(true);
        setEditingSection('address');
        setEditingData(team?.address || {});
        return;
      }
    }
    setStripeSetupLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(CREATE_ACCOUNT_LINK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          teamId: id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Unable to open Stripe.');
      }
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error('Stripe link not available.');
    } catch (err) {
      alert(err?.message || 'Unable to open Stripe. Try again later.');
    } finally {
      setStripeSetupLoading(false);
    }
  };
  const openSubscriptionsEditor = ({ skipAddressCheck = false } = {}) => {
    if (isClubManagedMembershipMode(savedMembershipMode)) {
      navigate(`/teams/${id}/club-billing`);
      return;
    }
    if (!skipAddressCheck) {
      const teamCountry = String(team?.address?.country || '').trim();
      if (!teamCountry) {
        setPendingSubscriptionsAfterAddress(true);
        setEditingSection('address');
        setEditingData(team?.address || {});
        return;
      }
    }
    setEditingSection('subscriptions');
    setEditingData(team?.subscriptions || {});
  };
  const handleOpenLaunchChecklistItem = (key) => {
    setClubLaunchOpen(false);
    if (key === 'address') {
      setEditingSection('address');
      setEditingData(team?.address || {});
    } else if (key === 'profile') {
      openSubscriptionsEditor();
    } else if (key === 'metadata') {
      setEditingSection('teamMetadata');
      setEditingData(team?.teamMetadata || {});
    } else if (key === 'joinForm') {
      setEditingSection('joinFormConfig');
      setEditingData(team?.joinFormConfig || {});
    } else if (key === 'payments') {
      if (launchMembership.membershipMode !== 'hosted_stripe' && (launchMembership.membershipMode !== 'hybrid' || checklist?.payments?.completed === true)) navigate(`/teams/${id}/club-billing`);
      else handleStripeSetup();
    }
  };
  const handleOpenLaunchMembership = () => {
    setClubLaunchOpen(false);
    navigate(launchMembership.isPrelaunchCommittee
      ? `/join/${id}?manage=1&prelaunchCommittee=1&restartMembership=1`
      : `/join/${id}?manage=1`);
  };
  const handleOpenLaunchBilling = async () => {
    if (launchMembership.membershipMode !== 'hosted_stripe') {
      setClubLaunchOpen(false);
      navigate(`/teams/${id}/club-billing`);
      return;
    }
    if (!user) throw new Error('You must be signed in to manage billing.');
    const token = await user.getIdToken(true);
    const response = await fetch(getFunctionUrl('createStripePortalSession'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ teamId: id }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.url) {
      throw new Error(data?.error || 'Could not open billing.');
    }
    window.location.href = data.url;
  };
  const handleChooseLaunchMembershipMode = async (mode) => {
    if (!['hosted_stripe', 'hybrid', 'club_gated', 'club_pay_later'].includes(mode)) {
      throw new Error('Choose how membership payments will be handled.');
    }
    await withSaveTimeout(
      httpsCallable(functions, 'setMembershipHandlingMode')({
        teamId: id,
        mode,
        payLaterRequiresRoundAssignment: false,
      })
    );
    setTeam((current) =>
      current
        ? {
            ...current,
            membershipHandling: {
              ...(current.membershipHandling || {}),
              mode,
              setupChoiceConfirmed: true,
            },
          }
        : current
    );
    launchMembership.refresh();
  };
  const handleClubLaunch = async (nextAvailability) => {
    const launchedAvailability = {
      live: true,
      public: Boolean(nextAvailability?.public),
      joinable: ['Open', 'Closed', 'On Approval'].includes(nextAvailability?.joinable) ? nextAvailability.joinable : 'Closed',
    };
    await httpsCallable(functions, 'launchClub')({ teamId: id, availability: launchedAvailability });
    setTeam((current) =>
      current
        ? {
            ...current,
            availability: launchedAvailability,
            checklist: {
              ...(current.checklist || {}),
              access: { completed: true, updatedAt: new Date() },
            },
          }
        : current
    );
  };
  const updateField = async (field, value, { keepOpen = false } = {}) => {
    const cleanedValue = value !== undefined ? stripUndefinedDeep(value) : undefined;
    try {
      const teamRef = doc(db, 'teams', id);
      const updates = {};
      if (cleanedValue !== undefined) updates[field] = cleanedValue;
      const checklistMap = {
        address: 'address',
        subscriptions: 'profile',
        tags: 'tags',
        availability: 'access',
        brand: 'branding',
        teamMetadata: 'metadata',
        joinFormConfig: 'joinForm',
        stripe: 'payments',
      };
      if (field in checklistMap) {
        const key = checklistMap[field];
        updates[`checklist.${key}`] = {
          completed: true,
          updatedAt: serverTimestamp(),
        };
      }
      if (field === 'tags' && Array.isArray(value)) {
        const deletedTagNames = getDeletedTagNames(team?.tags || [], value);
        await removeDeletedTagsFromMembers(id, team?.tags || [], value);
        await pruneDeletedTagsFromPricingOverrides(id, deletedTagNames);
      }

      await updateDoc(teamRef, updates);

      if (field === 'address' && cleanedValue) {
        setTeam((prev) =>
          prev
            ? {
                ...prev,
                address: cleanedValue,
                checklist: {
                  ...(prev.checklist || {}),
                  address: {
                    completed: true,
                    updatedAt: new Date(),
                  },
                },
              }
            : prev
        );
      }

      if (!keepOpen) {
        setEditingSection(null);
        setEditingData(null);
      }
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Could not save club settings. Please try again.');
    }
  };
  const allCardDefinitions = [
    {
      id: 'prelaunchCommitteeSetup',
      label: 'Pre-launch Membership',
      visible: Boolean(prelaunchCommitteeStatus),
      forceVisible: Boolean(prelaunchCommitteeStatus),
      hideable: false,
      fixed: true,
      reorderable: false,
      render: () => (
        <article className="team-card">
          <div className="card-header-with-icon">
            <span className="card-title">Pre-launch Membership</span>
          </div>
          <div className="card-body">
            <p>
              {prelaunchCommitteeStatus?.status === 'paid'
                ? 'Your launch payment has succeeded. The club can go live once the remaining committee setup is ready.'
                : prelaunchCommitteeStatus?.status === 'payment_authorized'
                  ? 'Your membership and card are ready. Nothing is charged until the club’s first launch attempt.'
                  : prelaunchCommitteeStatus?.status === 'payment_failed'
                    ? 'Your launch payment needs attention before the club can go live.'
                    : 'Complete your membership details so the club can go live.'}
            </p>
            {prelaunchCommitteeStatus?.status !== 'paid' && (
              <Link className="button button-primary" to={`/join/${id}?manage=1&prelaunchCommittee=1&restartMembership=1`}>
                {prelaunchCommitteeStatus?.status === 'payment_authorized' ? 'Review membership setup' : 'Complete setup'}
              </Link>
            )}
          </div>
        </article>
      ),
    },
    {
      id: 'modules',
      label: 'Enabled Modules',
      visible: canManageSettings,
      hideable: false,
      fixed: true,
      reorderable: false,
      render: () => (
        <ModulesCard
          modules={modulesWithDefaults}
          metadata={teamMetadata}
          onEdit={() => {
            setEditingSection('modules');
            setEditingData(modules || {});
          }}
        />
      ),
    },
    {
      id: 'availability',
      label: availability?.live === true ? 'Access Controls' : 'Go Live',
      visible: canManageSettings,
      forceVisible: availability?.live !== true,
      hideable: true,
      reorderable: true,
      render: () => (
        <AvailabilityCard
          availability={availability}
          onEdit={() => {
            setEditingSection('availability');
            setEditingData(availability);
          }}
          onLaunch={() => setClubLaunchOpen(true)}
          teamId={id}
          checklist={checklist}
        />
      ),
    },
    {
      id: 'reporting',
      label: 'Reporting',
      visible: Boolean(canManageReports && modulesWithDefaults.reporting?.enabled),
      hideable: true,
      reorderable: true,
      render: () => <ReportingCard teamId={id} />,
    },
    {
      id: 'joinForm',
      label: 'Join Form',
      visible: canManageMembership,
      hideable: true,
      reorderable: true,
      render: () => (
        <JoinFormCard
          config={joinFormConfig}
          onEdit={() => {
            setEditingSection('joinFormConfig');
            setEditingData(joinFormConfig);
          }}
          documents={documents}
          documentsEnabled={Boolean(modulesWithDefaults.documents?.enabled)}
          refreshKey={0}
        />
      ),
    },
    {
      id: 'subscriptions',
      label: 'Membership Payments',
      visible: Boolean(canManageFinance || canManageMembership),
      hideable: true,
      reorderable: true,
      render: () => (
        <SubscriptionsCard
          subscriptions={subscriptions}
          onEdit={() => navigate(`/teams/${id}/club-billing`)}
          canEdit={canManageFinance || canManageMembership}
          teamId={id}
          refreshKey={0}
          memberProfiles={memberProfiles}
          membershipMode={savedMembershipMode}
        />
      ),
    },
    {
      id: 'membershipReconciliation',
      label: 'Membership Reconciliation',
      visible: canManageFinance && membershipReconciliationEnabled,
      hideable: true,
      reorderable: true,
      render: () => <MembershipReconciliationCard teamId={id} reconciliationEnabled />,
    },
    {
      id: 'discounts',
      label: 'Membership Discounts',
      visible: Boolean(canManageMembership && modulesWithDefaults.discounts?.enabled),
      hideable: true,
      reorderable: true,
      render: () => (
        <DiscountsCard
          teamId={id}
          onEdit={() => {
            setEditingSection('discounts');
            setEditingData(null);
          }}
        />
      ),
    },
    {
      id: 'boltons',
      label: 'Bolt-ons',
      visible: Boolean(modulesWithDefaults.boltons?.enabled),
      hideable: true,
      reorderable: true,
      render: () => (
        <BoltOnsCard
          onEdit={() => {
            requireStripePayment({
              feature: 'bolt_ons',
              featureLabel: 'Bolt-ons',
              returnTarget: 'bolt_ons',
              onReady: () => {
                setEditingSection('boltOns');
                setEditingData(null);
              },
            });
          }}
          canEdit={canManageMembership}
          teamId={id}
          refreshKey={0}
          memberProfiles={memberProfiles}
        />
      ),
    },
    {
      id: 'incomeDashboard',
      label: 'Income Overview',
      visible: canManageFinance,
      hideable: true,
      reorderable: true,
      render: () => <IncomeDashboardCard teamId={id} />,
    },
    {
      id: 'stripe',
      label: 'Stripe Account',
      visible: canManageFinance,
      hideable: true,
      reorderable: true,
      render: () => (
        <StripeCard
          teamId={id}
          onEdit={() => {
            setEditingSection('stripe');
            setEditingData(team.stripe || {});
          }}
          refreshKey={0}
          setStripeStatus={(info) => {
            if (info?.transfers_enabled && !checklist?.payments?.completed && !hasMarkedStripeComplete) {
              updateDoc(doc(db, 'teams', id), {
                'checklist.payments': {
                  completed: true,
                  updatedAt: serverTimestamp(),
                },
              }).then(() => setHasMarkedStripeComplete(true));
            }
          }}
        />
      ),
    },
    {
      id: 'metadata',
      label: 'Club Details',
      visible: canManageSettings,
      hideable: true,
      reorderable: true,
      render: () => (
        <MetadataCard
          name={name}
          description={description}
          metadata={teamMetadata}
          onEdit={() => {
            setEditingSection('teamMetadata');
            setEditingData(teamMetadata);
          }}
        />
      ),
    },
    {
      id: 'tags',
      label: 'Tags',
      visible: canManageMembers,
      hideable: true,
      reorderable: true,
      render: () => (
        <TagsCard
          isAdmin={canManageMembers}
          tags={tags}
          onEdit={() => {
            setEditingSection('tags');
            setEditingData(tags);
          }}
          teamId={id}
        />
      ),
    },
    {
      id: 'myTags',
      label: 'My Tags',
      visible: showMyTagsCard,
      hideable: true,
      reorderable: true,
      render: () => <MyTagsCard teamId={id} allTags={tags} memberTags={myMemberProfile?.tags || {}} />,
    },
    {
      id: 'manageMembership',
      label: 'Manage Membership',
      visible: Boolean(myMemberProfile || hasAdminAccess),
      hideable: true,
      reorderable: true,
      render: () => (
        <ManageMembershipCard
          teamId={id}
          teamName={name || 'this club'}
          memberName={myMemberProfile?.name || user?.displayName || user?.email || 'Member'}
          adminIds={team?.admins || []}
          adminNames={adminProfiles.map((admin) => admin.name).filter(Boolean)}
          allTags={tags}
          memberTags={myMemberProfile?.tags || {}}
          selfManaged={effectiveIsAdmin}
          membershipMode={savedMembershipMode}
          autoOpen={autoOpenMembershipManager}
          onAutoOpenHandled={handleMembershipAutoOpenHandled}
          canLeave={!effectiveIsAdmin}
          onLeave={openLeaveModal}
        />
      ),
    },
    {
      id: 'mentoring',
      label: 'Mentoring',
      visible: Boolean(uid && modulesWithDefaults.mentoring?.enabled),
      hideable: true,
      forceVisible: mentoringInvitationRequested,
      reorderable: true,
      render: () => (
        <MentoringCard
          teamId={id}
          userId={uid}
          isAdmin={canManageMentoring}
          settings={modulesWithDefaults.mentoring}
          members={memberProfiles}
          invitationRequested={mentoringInvitationRequested}
          onInvitationHandled={handleMentoringInvitationOpened}
        />
      ),
    },
    {
      id: 'rideReports',
      label: 'Ride Reports',
      visible: Boolean(uid && supportsIamRoadSmart && modulesWithDefaults?.iamRoadSmart?.rideReports?.enabled),
      hideable: true,
      forceVisible: Boolean(rideReportParam),
      reorderable: true,
      render: () => (
        <RideReportsCard
          teamId={id}
          userId={uid}
          canManage={canManageRideReports}
          settings={modulesWithDefaults.iamRoadSmart.rideReports}
          memberTags={myMemberProfile?.tags || {}}
          members={memberProfiles}
          tags={tags}
          initialReportId={rideReportParam}
          onInitialReportHandled={handleRideReportOpened}
        />
      ),
    },
    {
      id: 'rideDeclarations',
      label: 'Ride Declaration',
      visible: Boolean(uid && supportsIamRoadSmart && modulesWithDefaults?.iamRoadSmart?.rideDeclarations?.enabled),
      hideable: false,
      reorderable: true,
      render: () => (
        <RideDeclarationsCard
          teamId={id}
          team={team}
          userId={uid}
          member={myMemberProfile}
          members={memberProfiles}
          canManage={canManageRideDeclarations}
          onEditProfile={() => {
            setEditingSection('memberProfileFields');
            setEditingData(null);
          }}
        />
      ),
    },
    {
      id: 'branding',
      label: 'Branding',
      visible: Boolean(modulesWithDefaults.branding?.enabled && canManageSettings),
      hideable: true,
      reorderable: true,
      render: () => (
        <BrandingCard
          brand={brand}
          clubUrlSlug={team?.clubUrlSlug || ''}
          onEdit={() => {
            setEditingSection('brand');
            setEditingData(brand);
          }}
        />
      ),
    },
    {
      id: 'events',
      label: 'Events',
      visible: Boolean(modulesWithDefaults.events?.enabled),
      hideable: true,
      reorderable: true,
      render: () => (
        <EventsCard
          events={upcomingEvents}
          isAdmin={canManageEvents}
          canCheckIn={canCheckInEvents}
          teamId={id}
          memberTags={myMemberProfile?.tags || []}
          publicEventsSlug={team?.publicEventsSlug || ''}
          onOpenCalendar={() =>
            navigate(`/teams/${id}/calendar`, {
              state: {
                teamName: team.name,
                teamId: id,
                userId: user.uid,
                isAdmin: canManageEvents,
              },
            })
          }
          onOpenEvent={(event) => {
            const eventId = event?.originalId || event?.id;
            if (!eventId) return;
            const occurrence = event?.occurrenceId ? `&occurrence=${encodeURIComponent(event.occurrenceId)}` : '';
            navigate(`/teams/${id}/calendar?event=${encodeURIComponent(eventId)}${occurrence}`, {
              state: {
                teamName: team.name,
                teamId: id,
                userId: user.uid,
                isAdmin: canManageEvents,
              },
            });
          }}
          onOpenEventSetup={() => {
            if (!canManageEvents) return;
            setEditingSection('eventSetup');
            setEditingData(null);
          }}
        />
      ),
    },
    {
      id: 'courses',
      label: 'Courses',
      visible: Boolean(modulesWithDefaults.courses?.enabled),
      hideable: true,
      reorderable: true,
      render: () => (
        <CoursesCard
          teamId={id}
          isAdmin={canManageCourses}
          publicEventsSlug={team?.publicEventsSlug || ''}
          onEdit={(course) => {
            if (!canManageCourses) return;
            setEditingSection('courses');
            setEditingData({ ...(course || {}), canManageBookingForms });
          }}
        />
      ),
    },
    {
      id: 'externalEventAttendees',
      label: 'External Event Attendees',
      visible: Boolean(modulesWithDefaults.events?.enabled && team?.publicEventsSlug && (canManageEvents || canManageMembers)),
      hideable: true,
      reorderable: true,
      render: () => <ExternalEventAttendeesCard teamId={id} />,
    },
    {
      id: 'address',
      label: 'Address',
      visible: true,
      hideable: true,
      reorderable: true,
      render: () => (
        <AddressCard
          address={address}
          onEdit={() => {
            setEditingSection('address');
            setEditingData(address);
          }}
          isAdmin={canManageSettings}
        />
      ),
    },
    {
      id: 'publicLinks',
      label: 'Public Links',
      visible: Boolean(modulesWithDefaults.links?.enabled),
      hideable: true,
      reorderable: true,
      render: () => (
        <PublicLinksCard
          isAdmin={canManageSettings}
          links={publicLinks}
          onEdit={() => {
            setEditingSection('publicLinks');
            setEditingData(publicLinks);
          }}
        />
      ),
    },
    {
      id: 'documents',
      label: 'Documents',
      visible: Boolean(modulesWithDefaults.documents?.enabled),
      hideable: true,
      reorderable: true,
      render: () => (
        <DocumentsCard
          isAdmin={canManageDocuments}
          isMember={Boolean(myMemberProfile)}
          documents={documents}
          onEdit={() => {
            setEditingSection('documents');
            setEditingData(documents);
          }}
        />
      ),
    },
    {
      id: 'admins',
      label: 'Admins',
      visible: true,
      hideable: true,
      reorderable: true,
      render: () => <AdminListCard admins={adminProfiles} enableMessages={messagingEnabled} teamId={id} />,
    },
    {
      id: 'adminPermissions',
      label: 'Admin Permissions',
      visible: Boolean(effectiveIsAdmin && modulesWithDefaults.permissions?.enabled),
      hideable: true,
      reorderable: true,
      render: () => (
        <AdminPermissionsCard
          roles={team?.adminRoles || []}
          assignments={team?.adminAssignments || []}
          memberProfiles={memberProfiles}
          fullAdminIds={team?.admins || []}
          onEdit={() => {
            setEditingSection('adminPermissions');
            setEditingData(null);
          }}
        />
      ),
    },
    {
      id: 'adminActions',
      label: 'Team Admin Actions',
      visible: Boolean(canShowAdminActionsCard),
      hideable: true,
      reorderable: true,
      forceVisible: Boolean(shouldForceShowAdminActionsCard),
      render: () => (
        <AdminActionsCard
          teamId={id}
          pendingMembers={pendingMembers || []}
          waitlistMembers={waitlistMembers || []}
          learnToRowApprovalMembers={learnToRowApprovalMembers || []}
          learnToSailApprovalMembers={learnToSailApprovalMembers || []}
          pendingMembershipRequests={pendingMembershipRequests || []}
          membershipRequestsLoading={membershipRequestsLoading}
          pendingRoleRequests={pendingRoleRequests || []}
          roleRequestsLoading={roleRequestsLoading}
          onOpenPendingMembers={() => {
            setEditingSection('pendingMembers');
            setEditingData(pendingMembers);
          }}
          onOpenWaitlist={() => {
            setEditingSection('waitlist');
            setEditingData(waitlistMembers);
          }}
          onOpenLearnToRowWaitlist={() => {
            setEditingSection('learnToRow');
            setEditingData({ initialPanel: 'waitlist' });
          }}
          onOpenLearnToSailWaitlist={() => {
            setEditingSection('learnToSail');
            setEditingData({ initialPanel: 'waitlist' });
          }}
          memberPreviewLink={memberPreviewLink}
          joinFormDemoLink={joinFormDemoLink}
          canDeleteTeam={canDeleteTeam}
          onDeleteTeam={openDeleteModal}
          pendingDeletion={
            deletionPending
              ? {
                  executeAfter: pendingDeletionExecuteAfter,
                  requestedByName: pendingDeletionRequestedByName,
                  inProgressAt: pendingDeletionInProgressAt,
                }
              : null
          }
          canCancelDeleteTeam={canCancelDeleteTeam}
          onCancelDeleteTeam={openCancelDeleteModal}
          deleteBusy={deleteSubmitting}
          cancelDeleteBusy={cancelDeleteSubmitting}
        />
      ),
    },
    {
      id: 'waitlist',
      label: 'Waitlist',
      visible: Boolean(canManageMembership && (waitlistModuleEnabled || (waitlistMembers?.length || 0) > 0)),
      hideable: true,
      reorderable: true,
      forceVisible: Boolean(canManageMembership && (waitlistModuleEnabled || (waitlistMembers?.length || 0) > 0)),
      render: () => (
        <WaitlistCard
          waitlistMembers={waitlistMembers || []}
          onEdit={() => {
            setEditingSection('waitlist');
            setEditingData(waitlistMembers || []);
          }}
          joinRoutingEnabled={waitlistJoinRoutingEnabled}
        />
      ),
    },
    {
      id: 'members',
      label: 'Members',
      visible: canUseMemberDirectory,
      hideable: true,
      reorderable: true,
      render: () => (
        <MemberListCard
          members={memberProfiles}
          teamId={id}
          isAdmin={canManageMembers}
          canImportMembers={canManageMembers}
          canExportMembers={canExportMembers}
          canEmailMembers={emailEnabled && canManageCommunications}
          profileFieldsEnabled={profileFieldsEditable}
          showProfileSearchButton={adminProfileSearchEnabled}
          onOpenProfileSearch={() => setProfileSearchOpen(true)}
          onOpenMemberProfile={(member) => {
            setEditingSection(member?.id === user?.uid ? 'memberProfileFields' : 'adminMemberProfileFields');
            setEditingData(member);
          }}
          onEdit={(member) => {
            setEditingSection('member');
            setEditingData(member);
          }}
          enableMessages={messagingEnabled && canManageCommunications}
          onImportMembersClick={() => {
            setEditingSection('importMembers');
            setEditingData(null);
          }}
          onInviteMembersClick={() => {
            setEditingSection('inviteMembers');
            setEditingData(null);
          }}
          onExportMembersClick={() => {
            setEditingSection('exportMembers');
            setEditingData(null);
          }}
          onEmailMember={(member) => {
            setComposerMemberId(member?.id || '');
            setComposerOpen(true);
          }}
        />
      ),
    },
    {
      id: 'profileFields',
      label: 'Profile Details',
      visible: Boolean(modulesWithDefaults.profile?.enabled || profileCardForcedVisible),
      hideable: true,
      reorderable: true,
      forceVisible: profileCardForcedVisible || new URLSearchParams(location.search).get('panel') === 'qualifications',
      render: () => (
        <ProfileFieldsCard
          config={team?.profileFieldsConfig}
          isAdmin={canManageMembers}
          teamId={id}
          memberId={uid}
          memberProfileFields={myProfileFields}
          profileUpdateRequested={profileCardForcedVisible}
          profileUpdateRequestedAt={profileUpdateRequestedAt}
          onEditConfig={() => {
            setEditingSection('profileFieldsConfig');
            setEditingData(null);
          }}
          onEditMine={() => {
            setEditingSection('memberProfileFields');
            setEditingData(null);
          }}
        />
      ),
    },
    {
      id: 'demographics',
      label: effectiveIsAdmin ? 'Demographic Reporting' : 'Demographic Profile',
      visible: Boolean(uid && modulesWithDefaults.demographics?.enabled),
      hideable: true,
      reorderable: true,
      forceVisible: new URLSearchParams(location.search).get('panel') === 'demographics',
      render: () => (
        <DemographicsCard
          teamId={id}
          isFullAdmin={effectiveIsAdmin}
        />
      ),
    },
    {
      id: 'messageBoards',
      label: 'Message Boards',
      visible: Boolean(boardSurfaceAvailable),
      hideable: true,
      reorderable: true,
      forceVisible: Boolean(canManageCommunications && openBoardReviewCaseCount > 0),
      render: () => (
        <MessageBoardsCard
          teamId={id}
          isAdmin={canManageCommunications}
          openReviewCases={openBoardReviewCases}
          reviewCasesLoading={boardReviewCasesLoading}
          onOpenGifSettings={() => {
            setEditingSection('boardGifSettings');
            setEditingData({
              gifs: team?.communicationSettings?.gifs || {},
              boards: team?.communicationSettings?.boards || {},
            });
          }}
        />
      ),
    },
    {
      id: 'emailBroadcasts',
      label: 'Emails',
      visible: Boolean(emailEnabled && canManageCommunications),
      hideable: true,
      reorderable: true,
      render: () => (
        <EmailBroadcastsCard
          teamId={id}
          isAdmin={canManageCommunications}
          onCompose={() => {
            setComposerMemberId('');
            setComposerOpen(true);
          }}
          onOpenCampaign={(c) => {
            setSelectedCampaign(c);
            setEmailModalOpen(true);
          }}
        />
      ),
    },
    {
      id: 'inboxBroadcasts',
      label: 'Inbox Broadcasts',
      visible: Boolean(inboxBroadcastsAvailable && canManageCommunications),
      hideable: true,
      reorderable: true,
      render: () => (
        <InboxBroadcastsCard
          teamId={id}
          isAdmin={canManageCommunications}
          refreshKey={inboxBroadcastRefreshKey}
          onCompose={() => setInboxBroadcastComposerOpen(true)}
          onOpenBroadcast={(broadcast) => {
            setSelectedInboxBroadcast(broadcast);
            setInboxBroadcastDetailsOpen(true);
          }}
        />
      ),
    },
    {
      id: 'boatBooking',
      label: 'Boat Booking',
      visible: Boolean(supportsRowing && isRowingBoatBookingEnabled(modulesWithDefaults)),
      hideable: true,
      reorderable: true,
      render: () => (
        <BoatBookingCard
          teamId={id}
          isAdmin={canManageRowingBoatBooking}
          openCalendar={new URLSearchParams(location.search).get('boatCalendar') === 'rowing'}
          teamMetadata={teamMetadata}
          modules={modulesWithDefaults}
          rowing={rowing || {}}
          rules={rowing?.rules || {}}
          onEditRules={() => {
            setEditingSection('boatBookingRules');
            setEditingData(rowing?.rules || {});
          }}
          onRequestBoat={() => {
            setEditingSection('boatRequest');
            setEditingData(null);
          }}
        />
      ),
    },
    {
      id: 'rowingBoats',
      label: 'Boats',
      visible: Boolean(supportsRowing && canManageRowingFleet && isRowingFleetEnabled(modulesWithDefaults)),
      hideable: true,
      reorderable: true,
      render: () => (
        <RowingBoatsCard
          teamId={id}
          isAdmin={canManageRowingFleet}
          teamMetadata={teamMetadata}
          modules={modulesWithDefaults}
          rowing={rowing || {}}
          canManageMaintenance={canManageRowingBoatMaintenance && isRowingBoatMaintenanceEnabled(modulesWithDefaults)}
          onCreateMaintenanceTicket={(boat) => openMaintenanceTicketModal('rowing', boat)}
          onEdit={() => {
            setEditingSection('rowingBoats');
            setEditingData(rowing?.resources || []);
          }}
          onEditBoat={(boat) => {
            setEditingSection('rowingBoats');
            setEditingData({
              focusBoatId: boat?.id || null,
            });
          }}
        />
      ),
    },
    {
      id: 'boatMaintenance',
      label: 'Boat Maintenance',
      visible: Boolean(
        (supportsRowing &&
          isRowingBoatMaintenanceEnabled(modulesWithDefaults) &&
          (canManageRowingBoatMaintenance ||
            (uid && modulesWithDefaults?.rowing?.boatMaintenance?.allowMemberReports === true) ||
            (uid && modulesWithDefaults?.rowing?.boatMaintenance?.allowMemberTicketViewing === true))) ||
        (supportsSailing &&
          isSailingBoatMaintenanceEnabled(modulesWithDefaults) &&
          (canManageSailingBoatMaintenance ||
            (uid && modulesWithDefaults?.sailing?.boatMaintenance?.allowMemberReports === true) ||
            (uid && modulesWithDefaults?.sailing?.boatMaintenance?.allowMemberTicketViewing === true)))
      ),
      forceVisible: Boolean(maintenanceTicketRequest),
      hideable: true,
      reorderable: true,
      render: () => (
        <BoatMaintenanceCard
          teamId={id}
          modules={modulesWithDefaults}
          allowedSports={boatMaintenanceSports}
          canManageRowing={supportsRowing && canManageRowingBoatMaintenance}
          canManageSailing={supportsSailing && canManageSailingBoatMaintenance}
          canReportRowing={supportsRowing && modulesWithDefaults?.rowing?.boatMaintenance?.allowMemberReports === true}
          canReportSailing={supportsSailing && modulesWithDefaults?.sailing?.boatMaintenance?.allowMemberReports === true}
          canViewRowingTickets={supportsRowing && modulesWithDefaults?.rowing?.boatMaintenance?.allowMemberTicketViewing === true}
          canViewSailingTickets={supportsSailing && modulesWithDefaults?.sailing?.boatMaintenance?.allowMemberTicketViewing === true}
          createTicketRequest={maintenanceTicketRequest}
          onCreateTicketRequestHandled={() => setMaintenanceTicketRequest(null)}
        />
      ),
    },
    {
      id: 'learnToRow',
      label: 'Learn to Row',
      visible: Boolean(supportsRowing && canManageRowing && modulesWithDefaults?.rowing?.learnToRow?.enabled),
      hideable: true,
      reorderable: true,
      render: () => (
        <LearnToRowCard
          isAdmin={canManageRowing}
          teamId={id}
          modules={modulesWithDefaults}
          onManage={(data) => {
            setEditingSection('learnToRow');
            setEditingData(data || {});
          }}
        />
      ),
    },
    {
      id: 'learnToSail',
      label: 'Learn to Sail',
      visible: Boolean(supportsSailing && canManageSailing && modulesWithDefaults?.sailing?.learnToSail?.enabled),
      hideable: true,
      reorderable: true,
      render: () => (
        <LearnToSailCard
          isAdmin={canManageSailing}
          teamId={id}
          modules={modulesWithDefaults}
          onManage={(data) => {
            setEditingSection('learnToSail');
            setEditingData(data || {});
          }}
        />
      ),
    },
    {
      id: 'dutyRoster',
      label: 'Duty Roster',
      visible: Boolean(uid && supportsSailing && modulesWithDefaults?.sailing?.dutyRoster?.enabled),
      hideable: true,
      reorderable: true,
      render: () => (
        <DutyRosterCard
          teamId={id}
          userId={uid}
          isAdmin={canManageSailing}
          modules={modulesWithDefaults}
          onManage={(data) => {
            setEditingSection('dutyRoster');
            setEditingData({
              ...(data || {}),
              isAdmin: canManageSailing,
            });
          }}
        />
      ),
    },
    {
      id: 'moorings',
      label: 'Moorings',
      visible: Boolean(supportsSailing && modulesWithDefaults?.sailing?.moorings?.enabled && (canManageMoorings || (uid && mooringsMemberVisible))),
      hideable: true,
      reorderable: true,
      render: () => (
        <MooringsCard
          teamId={id}
          userId={uid}
          isAdmin={canManageMoorings}
          modules={modulesWithDefaults}
          memberProfiles={memberProfiles}
          onManage={(data) => {
            const nextData = data || { initialTab: 'map' };
            if (nextData.initialTab === 'payments') {
              requireStripePayment({
                feature: 'moorings_payments',
                featureLabel: 'Mooring payments',
                returnTarget: 'moorings_payments',
                onReady: () => {
                  setEditingSection('moorings');
                  setEditingData(nextData);
                },
              });
              return;
            }
            setEditingSection('moorings');
            setEditingData(nextData);
          }}
        />
      ),
    },
    {
      id: 'sailingClubBoats',
      label: 'Club Boats',
      visible: Boolean(supportsSailing && canManageSailingFleet && modulesWithDefaults?.sailing?.boats?.enabled),
      hideable: true,
      reorderable: true,
      render: () => (
        <ClubBoatsCard
          teamId={id}
          isAdmin={canManageSailingFleet}
          modules={modulesWithDefaults}
          canManageMaintenance={canManageSailingBoatMaintenance && isSailingBoatMaintenanceEnabled(modulesWithDefaults)}
          onCreateMaintenanceTicket={(boat) => openMaintenanceTicketModal('sailing', boat)}
          onEdit={() => {
            setEditingSection('sailingBoats');
            setEditingData({});
          }}
          onEditBoat={(boat) => {
            setEditingSection('sailingBoats');
            setEditingData({ focusBoatId: boat?.id || null });
          }}
        />
      ),
    },
    {
      id: 'sailingBoatBooking',
      label: 'Sailing Boat Booking',
      visible: Boolean(uid && supportsSailing && isSailingBoatBookingEnabled(modulesWithDefaults)),
      hideable: true,
      reorderable: true,
      render: () => (
        <SailingBoatBookingCard
          teamId={id}
          isAdmin={canManageSailingBoatBooking}
          openCalendar={new URLSearchParams(location.search).get('boatCalendar') === 'sailing'}
          modules={modulesWithDefaults}
          rules={getSailingRules(team || {})}
          onEditRules={() => {
            setEditingSection('sailingBoatBookingRules');
            setEditingData(getSailingRules(team || {}));
          }}
          onRequestBoat={() => {
            setEditingSection('sailingBoatRequest');
            setEditingData({});
          }}
        />
      ),
    },
    {
      id: 'sailingOutings',
      label: 'Club-led Outings',
      visible: Boolean(supportsSailing && canManageSailingOutings && isSailingOutingsEnabled(modulesWithDefaults)),
      hideable: true,
      reorderable: true,
      render: () => (
        <SailingOutingsCard
          isAdmin={canManageSailingOutings}
          teamId={id}
          modules={modulesWithDefaults}
          onManage={(data) => {
            setEditingSection('sailingOutings');
            setEditingData(data || { createNew: true });
          }}
        />
      ),
    },
    {
      id: 'mySailingOutings',
      label: 'My Club-led Outings',
      visible: Boolean(uid && supportsSailing && isSailingOutingsEnabled(modulesWithDefaults)),
      hideable: true,
      reorderable: true,
      render: () => (
        <MySailingOutingsCard
          teamId={id}
          userId={uid}
          modules={modulesWithDefaults}
          onOpenEvent={(event) => {
            const eventId = event?.originalId || event?.id;
            if (!eventId) return;
            navigate(`/teams/${id}/calendar?event=${encodeURIComponent(eventId)}`, {
              state: {
                teamName: team.name,
                teamId: id,
                userId: user.uid,
                isAdmin: canManageSailingOutings,
              },
            });
          }}
        />
      ),
    },
    {
      id: 'sailingCrewFinder',
      label: 'Crew Finder',
      visible: Boolean(uid && supportsSailing && isSailingCrewFinderEnabled(modulesWithDefaults)),
      hideable: true,
      reorderable: true,
      render: () => (
        <SailingCrewFinderCard
          teamId={id}
          userId={uid}
          modules={modulesWithDefaults}
          memberTags={myMemberProfile?.tags || {}}
          isAdmin={canManageSailingCrewFinder}
          onOpen={(data) => {
            setEditingSection('sailingCrewFinder');
            setEditingData({
              ...(data || {}),
              isAdmin: canManageSailingCrewFinder,
            });
          }}
        />
      ),
    },
    {
      id: 'outings',
      label: 'Plan Outings',
      visible: Boolean(supportsRowing && canManageRowingOutings && isRowingOutingsEnabled(modulesWithDefaults)),
      hideable: true,
      reorderable: true,
      render: () => (
        <OutingsCard
          isAdmin={canManageRowingOutings}
          teamId={id}
          modules={modulesWithDefaults}
          title="Plan Outings"
          onManage={(data) => {
            setEditingSection('outings');
            setEditingData(
              data || {
                createNew: true,
              }
            );
          }}
        />
      ),
    },
    {
      id: 'myOutings',
      label: 'My Outings',
      visible: Boolean(uid && supportsRowing && modulesWithDefaults?.rowing?.outings?.enabled),
      hideable: true,
      reorderable: true,
      render: () => (
        <MyOutingsCard
          teamId={id}
          userId={uid}
          modules={modulesWithDefaults}
          onOpenEvent={(event) => {
            const eventId = event?.originalId || event?.id;
            if (!eventId) return;
            const occurrence = event?.occurrenceId ? `&occurrence=${encodeURIComponent(event.occurrenceId)}` : '';
            navigate(`/teams/${id}/calendar?event=${encodeURIComponent(eventId)}${occurrence}`, {
              state: {
                teamName: team.name,
                teamId: id,
                userId: user.uid,
                isAdmin: canManageRowingOutings,
              },
            });
          }}
        />
      ),
    },
  ];
  const availableCards = allCardDefinitions.filter((card) => card.visible);
  const availableCardMap = buildCardMap(availableCards);
  const fixedCards = availableCards.filter((card) => card.fixed);
  const reorderableCardIds = availableCards.filter((card) => !card.fixed).map((card) => card.id);
  const eventOrderedCardIds = placeUnpositionedCardAfter(mergeCardOrder(cardPreferences.order, reorderableCardIds), cardPreferences.order, 'externalEventAttendees', 'events');
  const orderedReorderableCardIds = effectiveIsAdmin
    ? placeCardAfter(eventOrderedCardIds, 'demographics', 'reporting')
    : eventOrderedCardIds;
  const orderedCards = [...fixedCards, ...orderedReorderableCardIds.map((cardId) => availableCardMap[cardId]).filter(Boolean)];
  const hiddenCardIds = new Set(cardPreferences.hidden);
  const visibleCards = orderedCards.filter((card) => !card.hideable || card.forceVisible || !hiddenCardIds.has(card.id));
  const restrictedVisibleCards = filterCardsByRestriction(visibleCards, activeMemberCardRestriction);
  const gatedVisibleCards = courseGateActive ? visibleCards.filter((card) => card.id === 'manageMembership') : restrictedVisibleCards;
  return (
    <div
      className="teams-page club-home-page"
      style={{
        ...(primaryColor && {
          '--team-primary': primaryColor,
        }),
        ...(secondaryColor && {
          '--team-secondary': secondaryColor,
        }),
        ...(headingTextColor && {
          '--team-heading-text': headingTextColor,
        }),
        ...(bodyTextColor && {
          '--team-body-text': bodyTextColor,
        }),
      }}
    >
      <div className="teams-header-actions">
        <Link to="/teams" className="button button-secondary">
          Back to Clubs
        </Link>
        {isAdmin && viewingAsMember && (
          <Link to={adminViewLink} className="btn btn--secondary btn--sm">
            Back to Admin View
          </Link>
        )}
        <TeamCardPreferencesPanel
          cards={orderedCards}
          hiddenCardIds={hiddenCardIds}
          saving={cardPreferencesSaving || cardPreferencesLoading}
          onMoveCard={(cardId, direction) => {
            moveCard(cardId, direction, reorderableCardIds);
          }}
          onToggleHidden={(cardId, shouldHide) => {
            setCardHidden(cardId, shouldHide);
          }}
          onShowAll={() => {
            showAllCards();
          }}
          onReset={() => {
            resetPreferences();
          }}
        />
      </div>
      {stripeSetupLoading && <LoadingModal message="Fetching Stripe…" />}
      <PromptModal
        isOpen={legacyMetadataPromptOpen}
        title="Update Club Metadata"
        message="This club is still using the old details setup. Saving Club Details will migrate it onto the new categories and types flow."
        confirmLabel="Open Club Details"
        cancelLabel="Later"
        onConfirm={handleOpenLegacyMetadataEditor}
        onClose={handleCloseLegacyMetadataPrompt}
      />

      <div className={`card header-card${teamLogoUrl ? ' header-card--with-logo' : ''}`}>
        <div className="card-body header-card__body">
          {teamLogoUrl && (
            <div className="club-detail-logo">
              <img
                src={teamLogoUrl}
                alt={`${name} logo`}
                onError={(event) => {
                  event.currentTarget.closest('.club-detail-logo')?.style.setProperty('display', 'none');
                }}
              />
            </div>
          )}
          <div className="header-card__content">
            <h2>{name}</h2>
            {headerSubtitle && <p>{headerSubtitle}</p>}
          </div>
        </div>
      </div>

      {courseGateActive && (
        <div className="card learn-to-row-gate-banner">
          <div className="card-body learn-to-row-gate-banner__body">
            <div className="learn-to-row-gate-banner__content">
              <h3 className="learn-to-row-gate-banner__title">Your {courseGateLabel} access is paused</h3>
              <p className="learn-to-row-gate-banner__copy">Full club access is paused until you either set up full membership or leave the club.</p>
              {courseGatePurgeDateLabel && <p className="text-muted learn-to-row-gate-banner__copy">If you take no action, your access will be removed on {courseGatePurgeDateLabel}.</p>}
              <div className="learn-to-row-gate-banner__actions">
                <button className="btn btn--primary" type="button" onClick={() => navigate(`/teams/${id}?membership=manage`)}>
                  Apply for full membership
                </button>
                <button className="btn btn--secondary" type="button" onClick={openLeaveModal}>
                  Leave club
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`cards-container club-home-cards${courseGateActive ? ' cards-container--single-focus' : ''}`}>
        {gatedVisibleCards.map((card) => (
          <React.Fragment key={card.id}>{card.render()}</React.Fragment>
        ))}
      </div>

      {adminProfileSearchEnabled && (
        <ProfileSearchModal
          isOpen={profileSearchOpen}
          onClose={() => setProfileSearchOpen(false)}
          teamId={id}
          members={memberProfiles}
          profileConfig={team?.profileFieldsConfig}
          joinFormConfig={team?.joinFormConfig}
          allTags={tags}
          teamName={name}
          canEditProfiles={profileFieldsEditable}
          onOpenMember={(member) => {
            setProfileSearchOpen(false);
            setEditingSection(member?.id === user?.uid ? 'memberProfileFields' : 'adminMemberProfileFields');
            setEditingData(member);
          }}
        />
      )}

      <TeamEditModal
        key={`${editingSection}-0`}
        section={editingSection}
        data={editingData}
        onClose={() => {
          if (pendingStripeSetupAfterAddress && editingSection === 'address') {
            setPendingStripeSetupAfterAddress(false);
          }
          if (pendingSubscriptionsAfterAddress && editingSection === 'address') {
            setPendingSubscriptionsAfterAddress(false);
          }
          setEditingSection(null);
          setEditingData(null);
        }}
        onSave={async (v, saveOptions = {}) => {
          const section = editingSection;
          if (section === 'brand') {
            const result = await httpsCallable(functions, 'updateClubBranding')({ teamId: id, ...v });
            setTeam((current) => current ? { ...current, brand: result.data.brand, clubUrlSlug: result.data.clubUrlSlug } : current);
            setEditingSection(null);
            setEditingData(null);
          } else if (section === 'teamMetadata') {
            await updateDoc(doc(db, 'teams', id), {
              name: String(v?.name || '').trim(),
              description: String(v?.description || '').trim(),
              teamMetadata: v.teamMetadata || {},
              'checklist.metadata': {
                completed: true,
                updatedAt: serverTimestamp(),
              },
            });
          } else if (section === 'rowingBoats') {
            // sub-page manages
          } else if (section === 'learnToRow') {
            // sub-page manages
          } else if (section === 'learnToSail') {
            // sub-page manages
          } else if (section === 'dutyRoster') {
            // sub-page manages
          } else if (section === 'moorings') {
            // sub-page manages
          } else if (section === 'sailingBoats') {
            // sub-page manages
          } else if (section === 'sailingOutings') {
            // sub-page manages
          } else if (section === 'sailingCrewFinder') {
            // sub-page manages
          } else if (section === 'sailingBoatRequest') {
            // handled elsewhere
          } else if (section === 'outings') {
            // sub-page manages
          } else if (section === 'boatRequest') {
            // handled elsewhere
          } else if (section === 'eventSetup') {
            // managed by event setup subcollections
          } else if (section === 'adminPermissions') {
            // Admin permission forms persist via callable functions.
          } else if (section === 'boardGifSettings') {
            await updateDoc(doc(db, 'teams', id), {
              'communicationSettings.gifs': v?.gifs || {},
              'communicationSettings.boards.memberDeleteReviewEnabled': v?.boards?.memberDeleteReviewEnabled !== false,
            });
          } else if (section === 'member') {
            // Member edits are persisted by EditMemberForm via callable functions.
          } else if (section === 'boatBookingRules') {
            await updateDoc(doc(db, 'teams', id), {
              'rowing.rules': v,
            });
          } else if (section === 'sailingBoatBookingRules') {
            await updateDoc(doc(db, 'teams', id), {
              'sailing.rules': v,
            });
          } else if (section === 'subscriptions') {
            // Subscriptions form persists structured data directly; only mark checklist here.
            await updateDoc(doc(db, 'teams', id), {
              'checklist.profile': {
                completed: true,
                updatedAt: serverTimestamp(),
              },
            });
            if (v?.membershipYearReanchorSuggested) {
              setMembershipYearReanchorOpen(true);
            }
            if (v?.boltOnMembershipYearReanchorSuggested) {
              if (v?.membershipYearReanchorSuggested) setBoltOnReanchorAfterMembership(true);
              else setBoltOnMembershipYearReanchorOpen(true);
            }
          } else if (section === 'discounts') {
            // Discount form persists directly.
          } else {
            await updateField(section, v, saveOptions);
          }
          if (section === 'address' && pendingStripeSetupAfterAddress) {
            setPendingStripeSetupAfterAddress(false);
            const savedCountry = String(v?.country || '').trim();
            if (savedCountry) {
              await handleStripeSetup({
                skipAddressCheck: true,
              });
            }
          }
          if (section === 'address' && pendingSubscriptionsAfterAddress) {
            setPendingSubscriptionsAfterAddress(false);
            const savedCountry = String(v?.country || '').trim();
            if (savedCountry) {
              openSubscriptionsEditor({
                skipAddressCheck: true,
              });
            }
          }
        }}
        teamId={id}
        team={team}
        user={user}
        isFullAdmin={effectiveIsAdmin}
        memberTags={myMemberProfile?.tags || {}}
        memberProfiles={memberProfiles}
        canManageFinance={canManageFinance}
        canManageMembership={canManageMembership}
      />

      <MembershipYearReanchorModal isOpen={membershipYearReanchorOpen} onClose={() => {
        setMembershipYearReanchorOpen(false);
        if (boltOnReanchorAfterMembership) {
          setBoltOnReanchorAfterMembership(false);
          setBoltOnMembershipYearReanchorOpen(true);
        }
      }} teamId={id} brand={team?.brand || {}} onApplied={async () => {}} />
      <BoltOnMembershipYearReanchorModal isOpen={boltOnMembershipYearReanchorOpen} onClose={() => setBoltOnMembershipYearReanchorOpen(false)} teamId={id} currency={team?.currency || 'GBP'} onApplied={async () => {}} />

      <EmailCampaignDetailsModal
        isOpen={emailModalOpen}
        onClose={closeEmailCampaignModal}
        teamId={id}
        campaign={selectedCampaign}
        canReply={canManageCommunications}
        currentUserId={user?.uid || ''}
        memberProfiles={memberProfiles}
      />

      <EmailComposerModal
        isOpen={composerOpen}
        onClose={() => {
          setComposerOpen(false);
          setComposerMemberId('');
        }}
        teamId={id}
        tags={tags}
        members={memberProfiles}
        defaultAudienceMode={composerMemberId ? 'member' : 'tags'}
        defaultMemberId={composerMemberId}
        onSent={() => {
          setComposerOpen(false);
          setComposerMemberId('');
        }}
      />

      <InboxBroadcastDetailsModal
        isOpen={inboxBroadcastDetailsOpen}
        onClose={() => {
          setInboxBroadcastDetailsOpen(false);
          setSelectedInboxBroadcast(null);
        }}
        teamId={id}
        broadcast={selectedInboxBroadcast}
      />

      <InboxBroadcastComposerModal
        isOpen={inboxBroadcastComposerOpen}
        onClose={() => setInboxBroadcastComposerOpen(false)}
        teamId={id}
        tags={tags}
        members={memberProfiles}
        currentUserId={user?.uid || ''}
        onSent={(data) => {
          setInboxBroadcastComposerOpen(false);
          setInboxBroadcastRefreshKey((current) => current + 1);
          if (data?.broadcastId) {
            setSelectedInboxBroadcast(null);
          }
        }}
      />

      <ClubLaunchModal
        isOpen={clubLaunchOpen}
        teamName={name}
        checklist={checklist}
        permissionKeys={permissions.keys}
        availability={availability}
        membership={launchMembership}
        onClose={() => setClubLaunchOpen(false)}
        onOpenChecklistItem={handleOpenLaunchChecklistItem}
        onOpenMembership={handleOpenLaunchMembership}
        onManageBilling={handleOpenLaunchBilling}
        onChooseMembershipMode={handleChooseLaunchMembershipMode}
        canChooseMembershipMode={permissions.isFullAdmin}
        onLaunch={handleClubLaunch}
      />

      {deleteModalOpen && (
        <div className="modal-backdrop" onClick={closeDeleteModal}>
          <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="delete-team-title" onClick={(e) => e.stopPropagation()}>
            <h2 id="delete-team-title" className="modal-title">
              {deleteIsImmediate ? 'Delete club now?' : 'Schedule club deletion?'}
            </h2>
            <p>
              {deleteIsImmediate ? (
                <>
                  This will immediately delete <strong>{name || 'this club'}</strong>.<br></br> <br></br>
                  Members will be removed straight away, any club subscriptions will be cancelled, invites will be cleared, and all members will be emailed to let them know the club has been deleted.
                </>
              ) : (
                <>
                  This will schedule <strong>{name || 'this club'}</strong> for deletion in 24 hours.<br></br> <br></br>All admins will get an urgent email immediately. If nobody cancels it in time,
                  members will be removed, subscriptions will be cancelled, invites will be cleared, and the club data will be deleted.
                </>
              )}
            </p>
            {requiresManagedClosureSettlement && (
              <div className="club-deletion-billing-warning" role="note">
                <strong>A final Member Nest payment is required</strong>
                <p>
                  When deletion becomes final, we will close the club's current billing period and immediately charge its saved billing card for every account that has met the 24-hour qualification rule. The club will only be deleted if that payment succeeds.
                </p>
                <p>You can review the current count and projected total in Club Billing before continuing.</p>
                <label>
                  <input
                    type="checkbox"
                    checked={deleteBillingAccepted}
                    onChange={(event) => setDeleteBillingAccepted(event.target.checked)}
                    disabled={deleteSubmitting}
                  />
                  <span>I understand and authorise the final charge to the club's saved billing card.</span>
                </label>
              </div>
            )}
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Type the club name to confirm</span>
              <input
                type="text"
                className="form-input w-full"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={name || 'Club name'}
                disabled={deleteSubmitting}
                autoFocus
              />
            </label>
            <p className="text-muted small" style={{ marginTop: 0 }}>
              {deleteIsImmediate
                ? 'This club is eligible for immediate deletion because it is not live or only has one admin.'
                : `Scheduled deletion time: ${formatDateTime(pendingDeletionExecuteAfter) || '24 hours from now'}`}
            </p>
            {deleteError && <p className="modal-error">{deleteError}</p>}
            <div className="form-actions">
              <button type="button" className="button button-secondary" onClick={closeDeleteModal} disabled={deleteSubmitting}>
                Cancel
              </button>
              <button
                type="button"
                className="button button-danger"
                onClick={handleDeleteTeam}
                disabled={
                  deleteSubmitting ||
                  deleteConfirmName.trim() !== String(name || '').trim() ||
                  (requiresManagedClosureSettlement && !deleteBillingAccepted)
                }
              >
                {deleteSubmitting ? (deleteIsImmediate ? 'Deleting…' : 'Scheduling…') : deleteIsImmediate ? 'Delete now' : 'Schedule deletion'}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelDeleteModalOpen && (
        <div className="modal-backdrop" onClick={closeCancelDeleteModal}>
          <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="cancel-delete-team-title" onClick={(e) => e.stopPropagation()}>
            <h2 id="cancel-delete-team-title" className="modal-title">
              Cancel scheduled deletion?
            </h2>
            <p>
              This will cancel the scheduled deletion for <strong>{name || 'this club'}</strong> and notify every admin by notification and email.
            </p>
            {cancelDeleteError && <p className="modal-error">{cancelDeleteError}</p>}
            <div className="form-actions">
              <button type="button" className="button button-secondary" onClick={closeCancelDeleteModal} disabled={cancelDeleteSubmitting}>
                Keep deletion scheduled
              </button>
              <button type="button" className="button button-primary" onClick={handleCancelDeleteTeam} disabled={cancelDeleteSubmitting}>
                {cancelDeleteSubmitting ? 'Cancelling…' : 'Cancel deletion'}
              </button>
            </div>
          </div>
        </div>
      )}

      {leaveModalOpen && (
        <div className="modal-backdrop" onClick={closeLeaveModal}>
          <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="leave-club-title" onClick={(e) => e.stopPropagation()}>
            <h2 id="leave-club-title" className="modal-title">
              Leave club?
            </h2>
            <p>
              You are about to leave <strong>{name || 'this club'}</strong>. You will lose access to member-only content and billing for this club will be cancelled.<br></br> <br></br>You will not
              receive any refund for past payments, you can always rejoin later if you change your mind, but you will have to pay again if you rejoin.
            </p>
            {leaveError && <p className="modal-error">{leaveError}</p>}
            <div className="form-actions">
              <button type="button" className="button button-secondary" onClick={closeLeaveModal} disabled={leaveSubmitting}>
                Cancel
              </button>
              <button type="button" className="button button-danger" onClick={handleLeaveTeam} disabled={leaveSubmitting}>
                {leaveSubmitting ? 'Leaving…' : 'Yes, leave club'}
              </button>
            </div>
          </div>
        </div>
      )}
      {canManageFinance && <XeroIntegrationModal isOpen={xeroRecoveryOpen} onClose={() => setXeroRecoveryOpen(false)} teamId={id} />}
      {stripeGuardDialog}
    </div>
  );
}
